/**
 * src/TechHubToolPage.js
 *
 * VISINVOKE Tech Hub - generic tool host.
 *
 * Resolves the tool from the :toolSlug route param against techHubConfig,
 * rechecks Technician Access at the content level, and renders the converted
 * tool. Tools that are registered but not yet converted show a calm pending
 * state. Unknown slugs show a graceful not-found state.
 *
 * Converted tools in this stage:
 *   common-fixes-reference   - searchable reference list
 *   problem-diagnostic-tree  - interactive guided diagnosis
 *
 * The legacy diagnostic tool wrote a history record to localStorage. That
 * behaviour is intentionally not carried forward. No local persistence is used
 * anywhere in this file.
 */
import React, { useState } from 'react';
import { glassSurface } from './config/glassTheme';
import { useParams, useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { useAuth } from './AuthContext';
import { hexA } from './RegistryComponents';
import { recordTechActivity } from './techActivity';
import {
  getTechHubTool,
  getTechnicianAccess,
  getLauncherState,
  TECH_HUB_BASE_ROUTE,
  COMMON_FIXES,
  DIAGNOSTICS,
  DIAGNOSTIC_CATEGORIES,
  ERROR_CATEGORIES,
  ERROR_DATABASE,
  DRIVER_GENERIC_GUIDANCE,
  DRIVER_IMPORTANT_NOTES,
  parseHardwareId,
  HC_SOCKETS,
  HC_CHIPSET_GROUPS,
  HC_RAM_TYPES,
  HC_GPUS,
  checkCompatibility,
  BN_CPUS,
  BN_GPUS,
  BN_RAM,
  BN_STORAGE,
  BN_USECASES,
  analyzeBottleneck,
  BUILD_COMPONENTS,
  BUILD_TYPES,
  BUILD_PRESETS,
  parseSupplierCsv,
  computeBuildTotals,
  GAMING_GAMES,
  GAMING_RESOLUTIONS,
  GAMING_FPS,
  GAMING_QUALITY,
  calculateGamingSpecs,
  WS_WORKLOADS,
  WS_BUDGETS,
  buildWorkstationSpec,
  UR_RAM_OPTIONS,
  UR_STORAGE_OPTIONS,
  UR_BUDGET_OPTIONS,
  UR_ISSUE_OPTIONS,
  generateUpgradeRecommendations,
  BUILD_SPEC_FIELDS,
  BUILD_SPEC_MAX_STORAGE,
  REFURB_CPU_GEN,
  REFURB_CPU_TIER,
  REFURB_RAM,
  REFURB_STORAGE,
  REFURB_GPU,
  REFURB_CONDITION,
  calculateRefurbValue,
  STRESS_TEST_SECTIONS,
  STRESS_STATUSES,
  STRESS_VERDICTS,
  QC_CATEGORIES,
  qcTotalItems,
  REPAIR_TYPES,
  REPAIR_CHECKLISTS,
  BURNIN_DURATIONS,
  BURNIN_OVERALL_STATUS,
  BURNIN_RUN_STATUS,
  BURNIN_PROGRAMS,
  getBurninProgram,
  BACKUP_LOCATIONS,
  BACKUP_SERVER_TYPES,
  BACKUP_DRIVE_TYPES,
  BACKUP_CONNECTION_TYPES,
  getBackupDriveSizes,
  BACKUP_FOLDERS,
  BACKUP_VERIFICATION_TESTS,
  BACKUP_RESULTS,
  getBackupResult,
  BACKUP_SCHEDULES,
  getBackupSchedule,
  HANDOVER_CHECKLIST,
  HANDOVER_CREDENTIAL_ITEMS,
  DR_DEVICE_TYPES,
  DR_DAMAGE_LEVELS,
  DR_URGENCY,
  DR_SCOPE,
  assessDataRecovery,
  SEC_CATEGORIES,
  secTotalItems,
  secRiskLevel,
  SEC_NEXT_STEPS,
  PP_MIN_LENGTHS,
  PP_COMPLEXITY,
  PP_EXPIRATION,
  PP_HISTORY,
  PP_LOCKOUT_THRESHOLD,
  PP_LOCKOUT_DURATION,
  PP_MFA,
  PP_SESSION_TIMEOUT,
  PP_STORAGE_POLICY,
  PP_PROHIBITED_TYPES,
  PP_COMPROMISE_STEPS,
  PP_BEST_PRACTICES,
  PP_COMPLIANCE_NOTES,
  buildPasswordPolicy,
  TTI_CATEGORIES,
  getTtiStatus,
  ttiToolCount,
  QUICK_START_STEPS,
  QUICK_START_NOTES,
  USER_GUIDE_GROUPS,
  USER_GUIDE_NOTES,
  TECH_HUB_HELP_SECTIONS,
  TECH_HUB_HELP_SAFETY,
} from './techHubConfig';

// ---------------------------------------------------------------------------
// Converted tool: Common Fixes Quick Reference.
// ---------------------------------------------------------------------------
function CommonFixesReference() {
  const [term, setTerm] = useState('');
  const q = term.toLowerCase();
  const filtered = COMMON_FIXES.filter(fix => {
    if (fix.title.toLowerCase().indexOf(q) !== -1) return true;
    for (let i = 0; i < fix.steps.length; i++) {
      if (fix.steps[i].toLowerCase().indexOf(q) !== -1) return true;
    }
    return false;
  });

  return (
    <div>
      <div style={t.searchWrap}>
        <input
          type="text"
          value={term}
          placeholder="Search fixes..."
          onChange={e => setTerm(e.target.value)}
          style={t.searchInput}
        />
      </div>
      <div style={t.fixList}>
        {filtered.map((fix, i) => (
          <div key={i} style={t.fixCard}>
            <div style={t.fixTitle}>{fix.title}</div>
            <div style={t.timeBadge}>{'\u23F1 Time: ' + fix.time}</div>
            <ol style={t.fixSteps}>
              {fix.steps.map((step, j) => (
                <li key={j} style={t.fixStep}>{step}</li>
              ))}
            </ol>
          </div>
        ))}
        {filtered.length === 0 ? (
          <div style={t.emptyNote}>No fixes match that search.</div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Problem Diagnostic Tree.
// ---------------------------------------------------------------------------
function DecisionStep({ decision }) {
  const [choice, setChoice] = useState(null);
  return (
    <div style={t.decisionBox}>
      <div style={t.decisionQuestion}>{'\u2753 ' + decision.question}</div>
      <div style={t.decisionButtons}>
        <button
          style={{ ...t.decisionBtn, ...(choice === 'yes' ? t.decisionBtnYes : {}) }}
          onClick={() => setChoice('yes')}
        >
          {'\u2705 Yes'}
        </button>
        <button
          style={{ ...t.decisionBtn, ...(choice === 'no' ? t.decisionBtnNo : {}) }}
          onClick={() => setChoice('no')}
        >
          {'\u274C No'}
        </button>
      </div>
      {choice ? (
        <div style={t.solutionBox}>
          <div style={t.solutionTitle}>{'\u{1F4A1} Next Steps'}</div>
          <p style={t.solutionText}>{choice === 'yes' ? decision.yes : decision.no}</p>
        </div>
      ) : null}
    </div>
  );
}

function DiagnosticFlow({ categoryKey, onReset }) {
  const diagnostic = DIAGNOSTICS[categoryKey];
  if (!diagnostic) return null;
  return (
    <div>
      <button style={t.backInlineBtn} onClick={onReset}>{'\u2190 Back to Problem Selection'}</button>
      <h2 style={t.flowTitle}>{diagnostic.title}</h2>
      {diagnostic.steps.map((step, i) => (
        <div key={i} style={t.stepCard}>
          <div style={t.stepHead}>
            <span style={t.stepNumber}>{step.number}</span>
            <span style={t.stepTitle}>{step.title}</span>
          </div>
          <p style={t.stepContent}>{step.content}</p>
          {step.tools ? (
            <div style={t.toolsNeeded}>
              <strong>{'\u{1F6E0} Tools Needed: '}</strong>{step.tools.join(', ')}
            </div>
          ) : null}
          {step.decision ? <DecisionStep decision={step.decision} /> : null}
          {step.warning ? (
            <div style={t.warningBox}>
              <strong>{'\u26A0 Important: '}</strong>{step.warning}
            </div>
          ) : null}
        </div>
      ))}
      {diagnostic.commonSolutions ? (
        <div style={t.commonBox}>
          <div style={t.commonTitle}>{'\u{1F4CA} Common Solutions for This Issue'}</div>
          <ul style={t.commonList}>
            {diagnostic.commonSolutions.map((sol, i) => (
              <li key={i} style={t.commonItem}>{sol}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ProblemDiagnosticTree() {
  const [active, setActive] = useState(null);
  if (active) {
    return <DiagnosticFlow categoryKey={active} onReset={() => setActive(null)} />;
  }
  return (
    <div>
      <h2 style={t.flowTitle}>Select Problem Category</h2>
      <div style={t.problemGrid}>
        {DIAGNOSTIC_CATEGORIES.map(cat => (
          <button key={cat.key} style={t.problemCard} onClick={() => setActive(cat.key)}>
            <div style={t.problemIcon}>{cat.icon}</div>
            <div style={t.problemName}>{cat.name}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Error Code Lookup.
// ---------------------------------------------------------------------------
function ErrorCodeLookup() {
  const [category, setCategory] = useState('bsod');
  const [term, setTerm] = useState('');
  const [expanded, setExpanded] = useState({});

  const q = term.toLowerCase();
  const guard = term.length < 2 && category === 'all';

  const results = [];
  if (!guard) {
    const keys = Object.keys(ERROR_DATABASE);
    for (let k = 0; k < keys.length; k++) {
      const cat = keys[k];
      if (category === 'all' || category === cat) {
        const list = ERROR_DATABASE[cat];
        for (let i = 0; i < list.length; i++) {
          const err = list[i];
          const hay = (err.code + ' ' + err.name + ' ' + err.description).toLowerCase();
          if (q === '' || hay.indexOf(q) !== -1) {
            results.push({ err: err, cat: cat });
          }
        }
      }
    }
  }

  const toggle = key => {
    setExpanded(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  return (
    <div>
      <div style={t.tabRow}>
        {ERROR_CATEGORIES.map(c => (
          <button
            key={c.key}
            style={{ ...t.tab, ...(category === c.key ? t.tabActive : {}) }}
            onClick={() => setCategory(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div style={t.searchWrap}>
        <input
          type="text"
          value={term}
          placeholder="Search by code, name, or description..."
          onChange={e => setTerm(e.target.value)}
          style={t.searchInput}
        />
      </div>

      {guard ? (
        <div style={t.emptyNote}>Enter at least 2 characters to search, or select a category above.</div>
      ) : results.length === 0 ? (
        <div style={t.emptyNote}>No errors found. Try a different search term.</div>
      ) : (
        <div>
          <div style={t.resultCount}>{'Found ' + results.length + ' error' + (results.length !== 1 ? 's' : '')}</div>
          <div style={t.fixList}>
            {results.map((row, index) => {
              const err = row.err;
              const key = row.cat + '-' + err.code + '-' + index;
              const isOpen = !!expanded[key];
              return (
                <div key={key} style={t.errorCard} onClick={() => toggle(key)}>
                  <div style={t.errorCode}>{err.code}</div>
                  <div style={t.errorName}>{err.name}</div>
                  <div style={t.errorDesc}>{err.description}</div>
                  {err.pattern ? <div style={t.beepPattern}>{'Beep Pattern: ' + err.pattern}</div> : null}
                  {err.frequency ? <div style={t.frequency}>{'Frequency: ' + err.frequency}</div> : null}
                  {isOpen ? (
                    <div style={t.errorDetails}>
                      <div style={t.causeBox}>
                        <div style={t.causeTitle}>{'\u26A0 Common Causes'}</div>
                        <ul style={t.detailList}>
                          {err.causes.map((c, j) => (<li key={j} style={t.detailItem}>{c}</li>))}
                        </ul>
                      </div>
                      <div style={t.solveBox}>
                        <div style={t.solveTitle}>{'\u2705 Solutions'}</div>
                        <ol style={t.detailList}>
                          {err.solutions.map((s, j) => (<li key={j} style={t.detailItem}>{s}</li>))}
                        </ol>
                      </div>
                    </div>
                  ) : null}
                  <div style={t.toggleText}>{isOpen ? '\u25B2 Click to hide solutions' : '\u25BC Click to show solutions'}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Driver Issue Resolver.
// ---------------------------------------------------------------------------
function DriverIssueResolver() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);

  const analyze = () => {
    if (!input.trim()) {
      setResult({ kind: 'empty' });
      return;
    }
    const parsed = parseHardwareId(input);
    if (!parsed) {
      setResult({ kind: 'invalid' });
      return;
    }
    setResult({ kind: 'ok', data: parsed });
  };

  let output = null;
  if (result && result.kind === 'empty') {
    output = <div style={t.driverInvalid}>Please enter a Hardware ID.</div>;
  } else if (result && result.kind === 'invalid') {
    output = (
      <div style={t.driverInvalid}>
        <div style={t.driverInvalidTitle}>{'\u274C Invalid Hardware ID Format'}</div>
        <p style={t.driverInvalidText}>A Hardware ID should contain VEN_ and DEV_ codes.</p>
        <p style={t.driverInvalidText}>Example: PCI\VEN_10DE&amp;DEV_2204</p>
      </div>
    );
  } else if (result && result.kind === 'ok') {
    const d = result.data;
    const guidance = d.guidance || DRIVER_GENERIC_GUIDANCE;
    const isGeneric = !d.guidance;
    output = (
      <div style={t.driverResult}>
        <div style={t.identBox}>
          <div style={t.identTitle}>{'\u2705 Hardware Identified'}</div>
          <p style={t.identLine}><strong>Vendor: </strong>{d.vendorName + ' (VEN_' + d.vendorID + ')'}</p>
          <p style={t.identLine}><strong>Device: </strong>{d.deviceName + ' (DEV_' + d.deviceID + ')'}</p>
          <p style={t.identLine}><strong>Full Hardware ID: </strong><code style={t.identCode}>{d.input}</code></p>
        </div>

        <div style={t.driverBox}>
          <div style={t.driverBoxTitle}>{'\u{1F527} Driver Reference'}</div>
          {isGeneric ? (
            <p style={t.driverBoxText}>{'Suggested search: ' + d.vendorName + ' ' + d.deviceName + ' driver'}</p>
          ) : (
            guidance.links.map((lnk, i) => (
              <p key={i} style={t.driverBoxText}>
                <strong>Reference link: </strong>
                <a
                  href={lnk.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => recordTechActivity({
                    event_type:  'DOWNLOAD_LINK_CLICKED',
                    item_type:   'download',
                    item_name:   lnk.label,
                    item_slug:   d.vendorID,
                    source:      'download_page',
                    source_page: 'tech_hub_tool_driver_reference',
                    metadata:    { manufacturer: d.vendorName, vendor_id: d.vendorID, device_id: d.deviceID },
                  })}
                  style={t.driverLink}
                >{lnk.label}</a>
              </p>
            ))
          )}
          {guidance.note ? <p style={t.driverBoxText}>{guidance.note}</p> : null}
        </div>

        <div style={t.stepsBox}>
          <div style={t.stepsBoxTitle}>{'\u{1F4CB} Installation Guidance'}</div>
          <ol style={t.detailList}>
            {guidance.steps.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
          </ol>
        </div>

        <div style={t.notesBox}>
          <div style={t.notesBoxTitle}>{'\u26A0 Important Notes'}</div>
          <ul style={t.detailList}>
            {DRIVER_IMPORTANT_NOTES.map((n, i) => (<li key={i} style={t.detailItem}>{n}</li>))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={t.driverInputSection}>
        <div style={t.driverInputLabel}>Enter Hardware ID</div>
        <p style={t.driverInputHint}>Find the Hardware ID in Device Manager then Properties then Details then Hardware IDs.</p>
        <textarea
          rows={3}
          value={input}
          placeholder="Example: PCI\VEN_10DE&DEV_2204&SUBSYS_147D1458&REV_A1"
          onChange={e => setInput(e.target.value)}
          style={t.driverTextarea}
        />
        <button style={t.driverBtn} onClick={analyze}>Identify Hardware and Find Drivers</button>
      </div>
      {output}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Hardware Compatibility Checker.
// ---------------------------------------------------------------------------
function ResultRow({ row }) {
  const palette = {
    compatible:   { bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.35)',  color: '#4ade80', mark: '\u2705' },
    warning:      { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.35)', color: '#fbbf24', mark: '\u26A0' },
    incompatible: { bg: 'rgba(239,68,68,0.10)',  border: 'rgba(239,68,68,0.4)',   color: '#f87171', mark: '\u274C' },
  };
  const p = palette[row.kind] || palette.warning;
  return (
    <div style={{ ...t.resultRow, background: p.bg, border: '1px solid ' + p.border }}>
      <div style={{ ...t.resultRowTitle, color: p.color }}>{p.mark + ' ' + row.title}</div>
      {row.lines.map((line, i) => (<p key={i} style={t.resultRowLine}>{line}</p>))}
    </div>
  );
}

function HardwareCompatibilityChecker() {
  const [form, setForm] = useState({ socket: '', chipset: '', ramType: '', ramSpeed: '', gpu: '', psu: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(checkCompatibility(form));

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>CPU Socket</label>
          <select style={t.formSelect} value={form.socket} onChange={e => set('socket', e.target.value)}>
            <option value="">Select...</option>
            {HC_SOCKETS.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Motherboard Chipset</label>
          <select style={t.formSelect} value={form.chipset} onChange={e => set('chipset', e.target.value)}>
            <option value="">Select...</option>
            {HC_CHIPSET_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.options.map(o => (<option key={o} value={o}>{o}</option>))}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>RAM Type</label>
          <select style={t.formSelect} value={form.ramType} onChange={e => set('ramType', e.target.value)}>
            <option value="">Select...</option>
            {HC_RAM_TYPES.map(r => (<option key={r} value={r}>{r}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>RAM Speed (MHz)</label>
          <input type="number" style={t.formInput} value={form.ramSpeed} placeholder="e.g. 3600" onChange={e => set('ramSpeed', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Graphics Card</label>
          <select style={t.formSelect} value={form.gpu} onChange={e => set('gpu', e.target.value)}>
            <option value="">Select...</option>
            {HC_GPUS.map(g => (<option key={g.value} value={g.value}>{g.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>PSU Wattage</label>
          <input type="number" style={t.formInput} value={form.psu} placeholder="e.g. 750" onChange={e => set('psu', e.target.value)} />
        </div>
        <button style={t.driverBtn} onClick={run}>Check Compatibility</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please fill in all fields.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <ResultRow row={{
            kind: result.allCompatible ? 'compatible' : 'incompatible',
            title: result.allCompatible ? 'All Components Compatible' : 'Compatibility Issues Found',
            lines: [result.allCompatible ? 'This build will work together. Review individual checks below.' : 'Some components are incompatible. Review details below.'],
          }} />
          {result.rows.map((row, i) => (<ResultRow key={i} row={row} />))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: System Bottleneck Analyzer.
// ---------------------------------------------------------------------------
function CompCard({ label, value, sub, good }) {
  const color = good ? '#4ade80' : '#fbbf24';
  const bg = good ? 'rgba(34,197,94,0.10)' : 'rgba(251,191,36,0.10)';
  const border = good ? 'rgba(34,197,94,0.35)' : 'rgba(251,191,36,0.35)';
  return (
    <div style={{ ...t.compCard, background: bg, border: '1px solid ' + border }}>
      <div style={{ ...t.compCardLabel, color: color }}>{label}</div>
      <div style={t.compCardValue}>{value}</div>
      <div style={t.compCardSub}>{sub}</div>
    </div>
  );
}

function SystemBottleneckAnalyzer() {
  const [form, setForm] = useState({ cpu: '', gpu: '', ram: '', storage: '', useCase: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(analyzeBottleneck(form));

  const headColor = r => (r.bottleneck === 'None' ? '#4ade80' : (r.severity === 'Severe' ? '#f87171' : '#fbbf24'));

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>CPU</label>
          <select style={t.formSelect} value={form.cpu} onChange={e => set('cpu', e.target.value)}>
            <option value="">Select CPU...</option>
            {BN_CPUS.map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>GPU</label>
          <select style={t.formSelect} value={form.gpu} onChange={e => set('gpu', e.target.value)}>
            <option value="">Select GPU...</option>
            {BN_GPUS.map(g => (<option key={g.value} value={g.value}>{g.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>RAM Amount</label>
          <select style={t.formSelect} value={form.ram} onChange={e => set('ram', e.target.value)}>
            <option value="">Select RAM...</option>
            {BN_RAM.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Storage Type</label>
          <select style={t.formSelect} value={form.storage} onChange={e => set('storage', e.target.value)}>
            <option value="">Select Storage...</option>
            {BN_STORAGE.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Primary Use</label>
          <select style={t.formSelect} value={form.useCase} onChange={e => set('useCase', e.target.value)}>
            <option value="">Select Use Case...</option>
            {BN_USECASES.map(u => (<option key={u.value} value={u.value}>{u.label}</option>))}
          </select>
        </div>
        <button style={t.driverBtn} onClick={run}>Analyze System</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please fill in all fields.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: headColor(result) }}>
            <div style={{ ...t.bnHeadTitle, color: headColor(result) }}>
              {result.bottleneck === 'None' ? '\u2705 System Balance: Excellent' : '\u26A0 Primary Bottleneck: ' + result.bottleneck}
            </div>
            <p style={t.bnHeadLine}><strong>Severity: </strong>{result.severity}</p>
            <p style={t.bnHeadLine}><strong>CPU Score: </strong>{result.cpuScore + '/100'}<strong>{'    GPU Score: '}</strong>{result.gpuScore + '/100'}</p>
          </div>

          <div style={t.compGrid}>
            <CompCard label="CPU" value={result.cpuLabel} sub={result.cpuScore > 70 ? 'Powerful processor' : 'Consider upgrade'} good={result.cpuScore > 70} />
            <CompCard label="GPU" value={result.gpuLabel} sub={result.gpuScore > 70 ? 'Strong graphics card' : 'Consider upgrade'} good={result.gpuScore > 70} />
            <CompCard label="RAM" value={result.ram + 'GB'} sub={result.ramBottleneck ? 'Insufficient for use case' : 'Adequate'} good={!result.ramBottleneck} />
            <CompCard label="Storage" value={result.storageLabel} sub={result.storageBottleneck ? 'Major bottleneck' : (result.storage === 'NVME' ? 'Optimal' : 'Good')} good={!result.storageBottleneck} />
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>{'\u{1F4A1} Upgrade Recommendations'}</div>
            <ol style={t.detailList}>
              {result.recommendations.map((rec, i) => (<li key={i} style={t.detailItem}>{rec}</li>))}
            </ol>
          </div>

          <div style={t.bnGainBox}>
            <div style={t.bnGainTitle}>{'\u{1F4CA} Expected Performance Gain'}</div>
            <p style={t.bnHeadLine}><strong>Top Priority Upgrade: </strong>{result.topUpgrade}</p>
            <p style={t.bnHeadLine}><strong>Estimated Performance Gain: </strong>{result.estimatedGain}</p>
            <p style={t.bnGainNote}>Actual gains vary by specific workloads and software.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: PC Build Configurator (technician build planning).
// ---------------------------------------------------------------------------
function emptyParts() {
  const p = {};
  for (let i = 0; i < BUILD_COMPONENTS.length; i++) {
    p[BUILD_COMPONENTS[i].key] = { name: '', price: '' };
  }
  return p;
}

function PCBuildConfigurator() {
  const [buildType, setBuildType] = useState('');
  const [parts, setParts] = useState(emptyParts());
  const [extras, setExtras] = useState({ name: '', price: '' });
  const [assemblyFee, setAssemblyFee] = useState('150');
  const [products, setProducts] = useState([]);
  const [importNote, setImportNote] = useState('');
  const [summary, setSummary] = useState(null);
  const fileRef = React.useRef(null);

  const setPart = (key, field, value) => {
    setParts(prev => {
      const next = Object.assign({}, prev);
      next[key] = Object.assign({}, next[key], { [field]: value });
      return next;
    });
  };

  const applyPreset = value => {
    setBuildType(value);
    if (!value || value === 'custom') return;
    const preset = BUILD_PRESETS[value];
    if (!preset) return;
    const next = emptyParts();
    const keys = Object.keys(preset);
    for (let i = 0; i < keys.length; i++) {
      next[keys[i]] = { name: preset[keys[i]].name, price: String(preset[keys[i]].price) };
    }
    setParts(next);
  };

  const onImportClick = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const onFileChosen = e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const parsed = parseSupplierCsv(String(ev.target.result));
        setProducts(parsed.products);
        if (parsed.products.length === 0) {
          setImportNote('No products could be read from that file. Check that it has a name column and a price column.');
        } else {
          setImportNote('Imported ' + parsed.products.length + ' products for this session.');
        }
      } catch (err) {
        setImportNote('That file could not be parsed as CSV.');
      }
    };
    reader.onerror = () => setImportNote('That file could not be read.');
    reader.readAsText(file);
    e.target.value = '';
  };

  const applyProduct = (key, value) => {
    const idx = parseInt(value, 10);
    if (isNaN(idx) || idx < 0 || idx >= products.length) return;
    const prod = products[idx];
    setParts(prev => {
      const next = Object.assign({}, prev);
      next[key] = { name: prod.name, price: String(prod.price) };
      return next;
    });
  };

  const generate = () => {
    setSummary(computeBuildTotals(parts, assemblyFee, extras));
  };

  const reset = () => {
    setBuildType('');
    setParts(emptyParts());
    setExtras({ name: '', price: '' });
    setAssemblyFee('150');
    setSummary(null);
  };

  const money = n => '$' + (parseFloat(n) || 0).toFixed(2);

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.csvRow}>
          <button style={t.csvBtn} onClick={onImportClick}>Import Supplier CSV</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFileChosen} />
          <span style={t.csvNote}>{importNote || 'Optional. Select a supplier price list to use its products as build parts for this session.'}</span>
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Build Type</label>
          <select style={t.formSelect} value={buildType} onChange={e => applyPreset(e.target.value)}>
            <option value="">Select...</option>
            {BUILD_TYPES.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
          </select>
        </div>

        {BUILD_COMPONENTS.map(c => (
          <div key={c.key} style={t.formGroup}>
            <label style={t.formLabel}>{c.label}</label>
            <input type="text" style={t.formInput} value={parts[c.key].name} placeholder="Component name" onChange={e => setPart(c.key, 'name', e.target.value)} />
            <div style={t.buildRowInline}>
              <input type="number" style={{ ...t.formInput, maxWidth: 160 }} value={parts[c.key].price} placeholder="Price" onChange={e => setPart(c.key, 'price', e.target.value)} />
              {products.length > 0 ? (
                <select style={{ ...t.formSelect, maxWidth: 260 }} value="" onChange={e => applyProduct(c.key, e.target.value)}>
                  <option value="">Use imported product...</option>
                  {products.map((p, i) => (<option key={i} value={i}>{p.name + (p.price ? '  (' + money(p.price) + ')' : '')}</option>))}
                </select>
              ) : null}
            </div>
          </div>
        ))}

        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Items (optional)</label>
          <textarea rows={2} style={t.driverTextarea} value={extras.name} placeholder="Fans, RGB, OS license, etc." onChange={e => setExtras(prev => Object.assign({}, prev, { name: e.target.value }))} />
          <input type="number" style={{ ...t.formInput, maxWidth: 160 }} value={extras.price} placeholder="Price" onChange={e => setExtras(prev => Object.assign({}, prev, { price: e.target.value }))} />
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Assembly / Setup (optional)</label>
          <input type="number" style={{ ...t.formInput, maxWidth: 160 }} value={assemblyFee} onChange={e => setAssemblyFee(e.target.value)} />
        </div>

        <div style={t.buildBtnRow}>
          <button style={t.driverBtn} onClick={generate}>Generate Build Planning Summary</button>
          <button style={t.resetBtn} onClick={reset}>Reset</button>
        </div>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Build Planning Summary</div>
            <p style={t.bnGainNote}>For technician planning only. Not a customer quote.</p>
            {BUILD_COMPONENTS.map(c => (
              parts[c.key].name ? (
                <div key={c.key} style={t.buildLine}>
                  <span style={t.buildLineLabel}>{c.label}</span>
                  <span style={t.buildLineName}>{parts[c.key].name}</span>
                  <span style={t.buildLinePrice}>{money(parts[c.key].price)}</span>
                </div>
              ) : null
            ))}
            {extras.name ? (
              <div style={t.buildLine}>
                <span style={t.buildLineLabel}>Additional Items</span>
                <span style={t.buildLineName}>{extras.name}</span>
                <span style={t.buildLinePrice}>{money(extras.price)}</span>
              </div>
            ) : null}
          </div>

          <div style={t.bnGainBox}>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Parts Subtotal</span>
              <span style={t.buildLinePrice}>{money(summary.partsTotal)}</span>
            </div>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Assembly / Setup</span>
              <span style={t.buildLinePrice}>{money(summary.assemblyFee)}</span>
            </div>
            <div style={{ ...t.buildLine, borderTop: '1px solid rgba(255,255,255,0.18)', marginTop: 6, paddingTop: 12 }}>
              <span style={{ ...t.buildLineLabel, fontSize: 16, color: '#ffffff' }}>Planning Total</span>
              <span style={{ ...t.buildLinePrice, fontSize: 18, color: '#4ade80' }}>{money(summary.total)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Gaming PC Calculator.
// ---------------------------------------------------------------------------
function GamingPCCalculator() {
  const [form, setForm] = useState({ gameType: '', resolution: '', targetFPS: '', quality: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(calculateGamingSpecs(form));

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Target Games</label>
          <select style={t.formSelect} value={form.gameType} onChange={e => set('gameType', e.target.value)}>
            <option value="">Select...</option>
            {GAMING_GAMES.map(g => (<option key={g.value} value={g.value}>{g.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Target Resolution</label>
          <select style={t.formSelect} value={form.resolution} onChange={e => set('resolution', e.target.value)}>
            <option value="">Select...</option>
            {GAMING_RESOLUTIONS.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Target FPS</label>
          <select style={t.formSelect} value={form.targetFPS} onChange={e => set('targetFPS', e.target.value)}>
            <option value="">Select...</option>
            {GAMING_FPS.map(f => (<option key={f.value} value={f.value}>{f.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Graphics Quality</label>
          <select style={t.formSelect} value={form.quality} onChange={e => set('quality', e.target.value)}>
            <option value="">Select...</option>
            {GAMING_QUALITY.map(q => (<option key={q.value} value={q.value}>{q.label}</option>))}
          </select>
        </div>
        <button style={t.driverBtn} onClick={run}>Calculate Required Specs</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please fill in all fields.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: '#4ade80' }}>
            <div style={{ ...t.bnHeadTitle, color: '#4ade80' }}>{'\u2705 Recommended Build'}</div>
            <p style={t.bnHeadLine}><strong>For: </strong>{result.summaryFor}</p>
          </div>

          <div style={t.compGrid}>
            <CompCard label="Graphics Card" value={result.gpuRec} sub="" good={true} />
            <CompCard label="Processor" value={result.cpuRec} sub="" good={true} />
            <CompCard label="Memory" value={result.ramRec} sub="" good={true} />
            <CompCard label="Storage" value={result.storageRec} sub="" good={true} />
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>{'\u{1F3AF} Expected Performance'}</div>
            <p style={t.bnHeadLine}><strong>Estimated FPS: </strong>{result.estimatedFPS}</p>
            <p style={t.bnGainNote}>Actual performance varies by specific game optimization and settings.</p>
          </div>

          <div style={t.bnGainBox}>
            <div style={t.bnGainTitle}>Approximate Hardware Cost Range</div>
            <p style={t.bnHeadLine}><strong>Planning estimate: </strong>{'$' + result.costLow + ' - $' + result.costHigh}</p>
            <p style={t.bnGainNote}>Approximate parts-only planning estimate. Not a quote.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>{'\u{1F4A1} Additional Recommendations'}</div>
            <ul style={t.detailList}>
              <li style={t.detailItem}><strong>Monitor: </strong>{result.monitor}</li>
              <li style={t.detailItem}><strong>PSU: </strong>{result.psu}</li>
              <li style={t.detailItem}><strong>Cooling: </strong>{result.cooling}</li>
              <li style={t.detailItem}><strong>Internet: </strong>{result.internet}</li>
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Workstation Builder (technician workstation planning).
// ---------------------------------------------------------------------------
function WorkstationBuilder() {
  const [form, setForm] = useState({ workload: '', budget: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(buildWorkstationSpec(form));

  const money = n => '$' + (parseInt(n, 10) || 0).toLocaleString();

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Primary Use Case</label>
          <select style={t.formSelect} value={form.workload} onChange={e => set('workload', e.target.value)}>
            <option value="">Select workload...</option>
            {WS_WORKLOADS.map(w => (<option key={w.value} value={w.value}>{w.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Budget Range</label>
          <select style={t.formSelect} value={form.budget} onChange={e => set('budget', e.target.value)}>
            <option value="">Select budget...</option>
            {WS_BUDGETS.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
          </select>
        </div>
        <button style={t.driverBtn} onClick={run}>Build Workstation Spec</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please select a workload type and a budget.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: '#7dd3fc' }}>
            <div style={{ ...t.bnHeadTitle, color: '#7dd3fc' }}>{result.name}</div>
            <p style={t.bnHeadLine}>{result.budget.toUpperCase() + ' Tier'}</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Core Components</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Processor</span><span style={t.buildLineName}>{result.config.cpu}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Memory</span><span style={t.buildLineName}>{result.config.ram}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Graphics</span><span style={t.buildLineName}>{result.config.gpu}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Storage</span><span style={t.buildLineName}>{result.config.storage}</span></div>
          </div>

          <div style={t.bnGainBox}>
            <div style={t.bnGainTitle}>Approximate Hardware Planning Estimate</div>
            <p style={t.bnHeadLine}><strong>Estimate: </strong>{money(result.config.cost)}</p>
            <p style={t.bnGainNote}>Approximate hardware planning estimate only. Not a quote.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Why This Configuration</div>
            <p style={t.bnHeadLine}>{result.notes}</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>{'\u{1F4A1} Additional Recommendations'}</div>
            <ul style={t.detailList}>
              {result.additional.map((a, i) => (<li key={i} style={t.detailItem}>{a}</li>))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Upgrade Recommendation.
// ---------------------------------------------------------------------------
function UpgradeRecommendation() {
  const [form, setForm] = useState({ cpu: '', gpu: '', ram: '', storage: '', budget: '', issue: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(generateUpgradeRecommendations(form));

  const priorityStyle = p => {
    if (p === 1) return { color: '#f87171', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.4)', label: 'HIGH PRIORITY' };
    if (p === 2) return { color: '#fbbf24', bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.4)', label: 'MEDIUM PRIORITY' };
    return { color: '#4ade80', bg: 'rgba(34,197,94,0.10)', border: 'rgba(34,197,94,0.4)', label: 'LOW PRIORITY' };
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>CPU</label>
          <input type="text" style={t.formInput} value={form.cpu} placeholder="e.g. Intel i5-7400" onChange={e => set('cpu', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>GPU</label>
          <input type="text" style={t.formInput} value={form.gpu} placeholder="e.g. GTX 1060 6GB" onChange={e => set('gpu', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>RAM</label>
          <select style={t.formSelect} value={form.ram} onChange={e => set('ram', e.target.value)}>
            <option value="">Select...</option>
            {UR_RAM_OPTIONS.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Storage</label>
          <select style={t.formSelect} value={form.storage} onChange={e => set('storage', e.target.value)}>
            <option value="">Select...</option>
            {UR_STORAGE_OPTIONS.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Budget</label>
          <select style={t.formSelect} value={form.budget} onChange={e => set('budget', e.target.value)}>
            <option value="">Select...</option>
            {UR_BUDGET_OPTIONS.map(b => (<option key={b.value} value={b.value}>{b.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Primary Issue</label>
          <select style={t.formSelect} value={form.issue} onChange={e => set('issue', e.target.value)}>
            <option value="">Select...</option>
            {UR_ISSUE_OPTIONS.map(i => (<option key={i.value} value={i.value}>{i.label}</option>))}
          </select>
        </div>
        <button style={t.driverBtn} onClick={run}>Generate Recommendations</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please fill in all fields.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>{'\u{1F4A1} Upgrade Strategy'}</div>
            <p style={t.bnHeadLine}><strong>Budget: </strong>{result.budgetLabel}</p>
            <p style={t.bnHeadLine}><strong>Primary Issue: </strong>{result.issueLabel}</p>
            <p style={t.bnGainNote}>Recommendations below are prioritised by impact and cost ratio.</p>
          </div>

          {result.recommendations.length === 0 ? (
            <div style={t.stepsBox}>
              <p style={t.bnHeadLine}>No high-impact upgrades identified for the selected system and issue. The current configuration is reasonable for the stated use.</p>
            </div>
          ) : null}

          {result.recommendations.map((rec, index) => {
            const ps = priorityStyle(rec.priority);
            return (
              <div key={index} style={{ ...t.resultRow, background: ps.bg, border: '1px solid ' + ps.border }}>
                <div style={t.urHead}>
                  <div style={{ ...t.resultRowTitle, color: ps.color, marginBottom: 0 }}>{(index + 1) + '. ' + rec.title}</div>
                  <span style={{ ...t.urBadge, background: ps.color }}>{ps.label}</span>
                </div>
                <p style={t.resultRowLine}><strong>Why: </strong>{rec.reason}</p>
                <p style={t.resultRowLine}><strong>Recommendation: </strong>{rec.recommendation}</p>
                <p style={t.resultRowLine}><strong>Approx cost (planning estimate): </strong>{rec.cost}</p>
                <p style={t.resultRowLine}><strong>Expected Benefit: </strong>{rec.benefit}</p>
                <p style={{ ...t.resultRowLine, marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8' }}><strong>Note: </strong>{rec.notes}</p>
              </div>
            );
          })}

          {result.recommendations.length > 0 ? (
            <div style={t.bnGainBox}>
              <div style={t.bnGainTitle}>Approximate Planning Total</div>
              <p style={t.bnHeadLine}><strong>Estimated starting total for all upgrades: </strong>{'from $' + result.totalCostLow}</p>
              <p style={t.bnGainNote}>Approximate planning estimate only. Not a quote. Start with high-priority upgrades for best impact.</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Build Spec Sheet (build specification documentation).
// ---------------------------------------------------------------------------
function BuildSpecSheet() {
  const [buildId, setBuildId] = useState('');
  const [fields, setFields] = useState(() => {
    const f = {};
    for (let i = 0; i < BUILD_SPEC_FIELDS.length; i++) f[BUILD_SPEC_FIELDS[i].key] = '';
    return f;
  });
  const [storage, setStorage] = useState([{ position: '', drive: '' }]);
  const [customDraft, setCustomDraft] = useState('');
  const [customItems, setCustomItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);

  const setField = (key, value) => setFields(prev => Object.assign({}, prev, { [key]: value }));

  const setStorageRow = (i, key, value) => {
    setStorage(prev => {
      const next = prev.slice();
      next[i] = Object.assign({}, next[i], { [key]: value });
      return next;
    });
  };
  const addStorage = () => {
    if (storage.length >= BUILD_SPEC_MAX_STORAGE) return;
    setStorage(prev => prev.concat([{ position: '', drive: '' }]));
  };
  const removeStorage = i => setStorage(prev => prev.filter((row, idx) => idx !== i));

  const addCustom = () => {
    const v = customDraft.trim();
    if (!v) return;
    setCustomItems(prev => prev.concat([v]));
    setCustomDraft('');
  };
  const removeCustom = i => setCustomItems(prev => prev.filter((item, idx) => idx !== i));

  const generate = () => {
    const usedStorage = storage.filter(s => s.drive.trim() !== '');
    setSummary({
      buildId: buildId.trim() || 'N/A',
      date: new Date().toLocaleDateString(),
      fields: Object.assign({}, fields),
      storage: usedStorage,
      customItems: customItems.slice(),
      notes: notes.trim(),
    });
  };

  const dash = '\u2014';

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Build ID / Reference (optional)</label>
          <input type="text" style={t.formInput} value={buildId} placeholder="Enter a build reference" onChange={e => setBuildId(e.target.value)} />
        </div>

        {BUILD_SPEC_FIELDS.map(f => (
          <div key={f.key} style={t.formGroup}>
            <label style={t.formLabel}>{f.label}</label>
            <input type="text" style={t.formInput} value={fields[f.key]} placeholder={f.label + ' (name / model)'} onChange={e => setField(f.key, e.target.value)} />
          </div>
        ))}

        <div style={t.formGroup}>
          <label style={t.formLabel}>{'Storage (up to ' + BUILD_SPEC_MAX_STORAGE + ' drives)'}</label>
          {storage.map((row, i) => (
            <div key={i} style={t.buildRowInline}>
              <input type="text" style={{ ...t.formInput, maxWidth: 200 }} value={row.position} placeholder="Position (e.g. C:)" onChange={e => setStorageRow(i, 'position', e.target.value)} />
              <input type="text" style={t.formInput} value={row.drive} placeholder="Drive (name / model)" onChange={e => setStorageRow(i, 'drive', e.target.value)} />
              {storage.length > 1 ? (
                <button style={t.smallRemoveBtn} onClick={() => removeStorage(i)}>Remove</button>
              ) : null}
            </div>
          ))}
          {storage.length < BUILD_SPEC_MAX_STORAGE ? (
            <button style={{ ...t.smallAddBtn, marginTop: 8 }} onClick={addStorage}>+ Add Drive</button>
          ) : null}
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Components (optional)</label>
          <div style={t.buildRowInline}>
            <input type="text" style={t.formInput} value={customDraft} placeholder="Additional item name" onChange={e => setCustomDraft(e.target.value)} />
            <button style={t.smallAddBtn} onClick={addCustom}>+ Add Item</button>
          </div>
          {customItems.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              {customItems.map((item, i) => (
                <div key={i} style={t.customItemRow}>
                  <span style={t.customItemText}>{item}</span>
                  <button style={t.smallRemoveBtn} onClick={() => removeCustom(i)}>Remove</button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Technician Notes (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={notes} placeholder="Build notes, test results, configuration details" onChange={e => setNotes(e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Build Specification Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Build Specification Summary</div>
            <p style={t.bnGainNote}>For technician planning and documentation. Not a customer quote.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Build ID</span><span style={t.buildLineName}>{summary.buildId}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Build Date</span><span style={t.buildLineName}>{summary.date}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Components</div>
            {BUILD_SPEC_FIELDS.map(f => (
              <div key={f.key} style={t.buildLine}>
                <span style={t.buildLineLabel}>{f.label}</span>
                <span style={t.buildLineName}>{summary.fields[f.key] ? summary.fields[f.key] : dash}</span>
              </div>
            ))}
            {summary.storage.map((s, i) => (
              <div key={i} style={t.buildLine}>
                <span style={t.buildLineLabel}>{'Storage' + (s.position ? ' (' + s.position + ')' : '')}</span>
                <span style={t.buildLineName}>{s.drive}</span>
              </div>
            ))}
          </div>

          {summary.customItems.length > 0 ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Additional Components</div>
              {summary.customItems.map((item, i) => (
                <div key={i} style={t.buildLine}>
                  <span style={t.buildLineLabel}>{'Item ' + (i + 1)}</span>
                  <span style={t.buildLineName}>{item}</span>
                </div>
              ))}
            </div>
          ) : null}

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Technician Notes</div>
              <p style={t.bnHeadLine}>{summary.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Refurbished PC Valuation.
// ---------------------------------------------------------------------------
function RefurbishedPricer() {
  const [form, setForm] = useState({ cpuGen: '', cpuTier: '', ram: '', storage: '', gpu: '', condition: '' });
  const [result, setResult] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));
  const run = () => setResult(calculateRefurbValue(form));

  const money = n => '$' + n;

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>CPU Generation</label>
          <select style={t.formSelect} value={form.cpuGen} onChange={e => set('cpuGen', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_CPU_GEN.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>CPU Tier</label>
          <select style={t.formSelect} value={form.cpuTier} onChange={e => set('cpuTier', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_CPU_TIER.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>RAM</label>
          <select style={t.formSelect} value={form.ram} onChange={e => set('ram', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_RAM.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Storage</label>
          <select style={t.formSelect} value={form.storage} onChange={e => set('storage', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_STORAGE.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>GPU</label>
          <select style={t.formSelect} value={form.gpu} onChange={e => set('gpu', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_GPU.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Condition</label>
          <select style={t.formSelect} value={form.condition} onChange={e => set('condition', e.target.value)}>
            <option value="">Select...</option>
            {REFURB_CONDITION.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <button style={t.driverBtn} onClick={run}>Estimate Refurbishment Value</button>
      </div>

      {result && result.missing ? (
        <div style={t.driverInvalid}>Please fill in all fields.</div>
      ) : null}

      {result && !result.missing ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: '#4ade80', textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: '#4ade80' }}>Estimated Refurbishment Value</div>
            <p style={t.refurbBig}>{money(result.finalValue)}</p>
            <p style={t.bnHeadLine}>{'Estimated range: ' + money(result.minValue) + ' - ' + money(result.maxValue)}</p>
            <p style={t.bnGainNote}>Planning estimate only, not a quote.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Valuation Breakdown</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>CPU Value</span><span style={t.buildLinePrice}>{money(result.cpuValue)}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>RAM Value</span><span style={t.buildLinePrice}>{'+' + money(result.ramValue)}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Storage Value</span><span style={t.buildLinePrice}>{'+' + money(result.storageValue)}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>GPU Value</span><span style={t.buildLinePrice}>{'+' + money(result.gpuValue)}</span></div>
            <div style={{ ...t.buildLine, borderTop: '1px solid rgba(255,255,255,0.14)', marginTop: 6, paddingTop: 10 }}>
              <span style={t.buildLineLabel}>Subtotal</span><span style={t.buildLinePrice}>{money(result.subtotal)}</span>
            </div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Condition Adjustment</span><span style={t.buildLinePrice}>{result.conditionPct + '%'}</span></div>
            <div style={t.buildLine}>
              <span style={{ ...t.buildLineLabel, fontSize: 15, color: '#ffffff' }}>Estimated Value</span>
              <span style={{ ...t.buildLinePrice, fontSize: 16, color: '#4ade80' }}>{money(result.finalValue)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Stress Test Protocol (technician stress test summary).
// ---------------------------------------------------------------------------
function statusMeta(status) {
  if (status === 'pass') return { color: '#4ade80', bg: 'rgba(34,197,94,0.16)', label: '\u2713 PASS' };
  if (status === 'fail') return { color: '#f87171', bg: 'rgba(239,68,68,0.16)', label: '\u2717 FAIL' };
  if (status === 'skip') return { color: '#fbbf24', bg: 'rgba(251,191,36,0.16)', label: '\u2298 SKIP' };
  return { color: '#94a3b8', bg: 'rgba(148,163,184,0.16)', label: 'Not Tested' };
}

function StressTestProtocol() {
  const today = new Date().toISOString().slice(0, 10);
  const [testDate, setTestDate] = useState(today);
  const [systemSpecs, setSystemSpecs] = useState('');
  const [sections, setSections] = useState(() => {
    const s = {};
    for (let i = 0; i < STRESS_TEST_SECTIONS.length; i++) {
      const sec = STRESS_TEST_SECTIONS[i];
      const vals = { status: '' };
      for (let f = 0; f < sec.fields.length; f++) vals[sec.fields[f].key] = '';
      s[sec.key] = vals;
    }
    return s;
  });
  const [verdict, setVerdict] = useState('');
  const [techComments, setTechComments] = useState('');
  const [techName, setTechName] = useState('');
  const [summary, setSummary] = useState(null);

  const setSecField = (secKey, fieldKey, value) => {
    setSections(prev => {
      const next = Object.assign({}, prev);
      next[secKey] = Object.assign({}, next[secKey], { [fieldKey]: value });
      return next;
    });
  };
  const setSecStatus = (secKey, status) => {
    setSections(prev => {
      const next = Object.assign({}, prev);
      next[secKey] = Object.assign({}, next[secKey], { status: status });
      return next;
    });
  };

  const generate = () => {
    setSummary({
      testDate: testDate || 'N/A',
      systemSpecs: systemSpecs.trim() || 'N/A',
      sections: Object.assign({}, sections),
      verdict: verdict,
      techComments: techComments.trim(),
      techName: techName.trim() || 'N/A',
    });
  };

  const verdictMeta = v => {
    if (v === 'fail') return { color: '#f87171', text: '\u2717 ISSUES FOUND' };
    if (v === 'pass_notes') return { color: '#fbbf24', text: '\u2713 PASS WITH NOTES' };
    if (v === 'pass') return { color: '#4ade80', text: '\u2713 SYSTEM STABLE' };
    return { color: '#94a3b8', text: 'No verdict recorded' };
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Test Date</label>
          <input type="date" style={t.formInput} value={testDate} onChange={e => setTestDate(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>System Specs</label>
          <textarea rows={2} style={t.driverTextarea} value={systemSpecs} placeholder="Brief system description (CPU, RAM, GPU)" onChange={e => setSystemSpecs(e.target.value)} />
        </div>

        {STRESS_TEST_SECTIONS.map(sec => (
          <div key={sec.key} style={t.stressSection}>
            <div style={t.stressHeader}>{sec.title}</div>
            <div style={t.stressHint}>{'Tool: ' + sec.tool + '   Duration: ' + sec.duration}</div>
            {sec.fields.map(f => (
              <div key={f.key} style={t.formGroup}>
                <label style={t.formLabel}>{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea rows={2} style={t.driverTextarea} value={sections[sec.key][f.key]} placeholder={f.placeholder} onChange={e => setSecField(sec.key, f.key, e.target.value)} />
                ) : (
                  <input type={f.type} style={t.formInput} value={sections[sec.key][f.key]} placeholder={f.placeholder} onChange={e => setSecField(sec.key, f.key, e.target.value)} />
                )}
              </div>
            ))}
            <div style={t.statusBtnRow}>
              {STRESS_STATUSES.map(st => {
                const active = sections[sec.key].status === st.value;
                const m = statusMeta(st.value);
                return (
                  <button
                    key={st.value}
                    style={{ ...t.statusBtn, ...(active ? { background: m.bg, borderColor: m.color, color: m.color } : {}) }}
                    onClick={() => setSecStatus(sec.key, st.value)}
                  >
                    {st.mark + ' ' + st.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div style={t.formGroup}>
          <label style={t.formLabel}>Final Verdict</label>
          <select style={t.formSelect} value={verdict} onChange={e => setVerdict(e.target.value)}>
            <option value="">Select...</option>
            {STRESS_VERDICTS.map(v => (<option key={v.value} value={v.value}>{v.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Technician Comments</label>
          <textarea rows={3} style={t.driverTextarea} value={techComments} placeholder="Overall stability assessment, recommendations, follow-up required?" onChange={e => setTechComments(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Tested By</label>
          <input type="text" style={t.formInput} value={techName} placeholder="Technician name" onChange={e => setTechName(e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Stress Test Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Stress Test Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Test Date</span><span style={t.buildLineName}>{summary.testDate}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>System</span><span style={t.buildLineName}>{summary.systemSpecs}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Technician</span><span style={t.buildLineName}>{summary.techName}</span></div>
          </div>

          {STRESS_TEST_SECTIONS.map(sec => {
            const vals = summary.sections[sec.key];
            const m = statusMeta(vals.status);
            return (
              <div key={sec.key} style={{ ...t.resultRow, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={t.urHead}>
                  <div style={{ ...t.resultRowTitle, marginBottom: 0, color: '#e2e8f0' }}>{sec.title}</div>
                  <span style={{ ...t.urBadge, background: m.color }}>{m.label}</span>
                </div>
                {sec.fields.map(f => (
                  vals[f.key] ? (<p key={f.key} style={t.resultRowLine}><strong>{f.label + ': '}</strong>{vals[f.key]}</p>) : null
                ))}
              </div>
            );
          })}

          <div style={{ ...t.bnHeadBox, borderColor: verdictMeta(summary.verdict).color, textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: verdictMeta(summary.verdict).color }}>{verdictMeta(summary.verdict).text}</div>
            {summary.techComments ? <p style={t.bnHeadLine}>{summary.techComments}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: QC Checklist (technician QC summary).
// ---------------------------------------------------------------------------
function QCChecklist() {
  const total = qcTotalItems();
  const [checked, setChecked] = useState({});
  const [systemDesc, setSystemDesc] = useState('');
  const [notes, setNotes] = useState('');
  const [inspector, setInspector] = useState('');
  const [summary, setSummary] = useState(null);

  const keyFor = (catId, i) => catId + '-' + i;
  const toggle = key => {
    setChecked(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const checkedCount = Object.keys(checked).length;
  const pct = Math.round((checkedCount / total) * 100);

  const generate = () => {
    const perCategory = QC_CATEGORIES.map(cat => {
      let c = 0;
      for (let i = 0; i < cat.items.length; i++) if (checked[keyFor(cat.id, i)]) c++;
      return { title: cat.title, checked: c, total: cat.items.length };
    });
    const status = pct === 100 ? 'PASS' : pct >= 90 ? 'PASS (Minor)' : 'REVIEW REQUIRED';
    const statusColor = pct === 100 ? '#4ade80' : pct >= 90 ? '#fbbf24' : '#f87171';
    setSummary({
      date: new Date().toLocaleDateString(),
      systemDesc: systemDesc.trim() || 'N/A',
      inspector: inspector.trim() || 'N/A',
      notes: notes.trim(),
      checkedCount: checkedCount,
      pct: pct,
      status: status,
      statusColor: statusColor,
      perCategory: perCategory,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.qcProgressWrap}>
          <div style={t.qcProgressTrack}>
            <div style={{ ...t.qcProgressFill, width: pct + '%' }}>{pct + '%'}</div>
          </div>
          <div style={t.qcProgressLabel}>{checkedCount + ' of ' + total + ' checks'}</div>
        </div>

        {QC_CATEGORIES.map(cat => (
          <div key={cat.id} style={t.qcCategory}>
            <div style={t.qcCategoryTitle}>{cat.icon + '  ' + cat.title}</div>
            {cat.items.map((item, i) => {
              const key = keyFor(cat.id, i);
              const on = !!checked[key];
              return (
                <label key={i} style={t.qcItem} onClick={() => toggle(key)}>
                  <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                  <span style={{ ...t.qcItemText, ...(on ? { color: '#94a3b8', textDecoration: 'line-through' } : {}) }}>{item}</span>
                </label>
              );
            })}
          </div>
        ))}

        <div style={t.formGroup}>
          <label style={t.formLabel}>System Description (optional)</label>
          <input type="text" style={t.formInput} value={systemDesc} placeholder="Brief system description" onChange={e => setSystemDesc(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>QC Notes (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={notes} placeholder="Any observations or follow-up items" onChange={e => setNotes(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Checked By</label>
          <input type="text" style={t.formInput} value={inspector} placeholder="Technician name" onChange={e => setInspector(e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate QC Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: summary.statusColor, textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: summary.statusColor }}>{summary.status}</div>
            <p style={t.bnHeadLine}>{summary.checkedCount + ' of ' + total + ' checks completed (' + summary.pct + '%)'}</p>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Quality Control Summary</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>QC Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>System</span><span style={t.buildLineName}>{summary.systemDesc}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Checked By</span><span style={t.buildLineName}>{summary.inspector}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Category Completion</div>
            {summary.perCategory.map((c, i) => (
              <div key={i} style={t.buildLine}>
                <span style={t.buildLineLabel}>{c.title}</span>
                <span style={t.buildLinePrice}>{c.checked + ' / ' + c.total}</span>
              </div>
            ))}
          </div>

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>QC Notes</div>
              <p style={t.bnHeadLine}>{summary.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Repair Checklist (technician repair-step checklists).
// ---------------------------------------------------------------------------
function RepairChecklist() {
  const [repairType, setRepairType] = useState('');
  const [checked, setChecked] = useState({});
  const [tech, setTech] = useState('');
  const [summary, setSummary] = useState(null);

  const list = repairType ? REPAIR_CHECKLISTS[repairType] : null;

  const changeType = value => {
    setRepairType(value);
    setChecked({});
    setSummary(null);
  };

  const keyFor = (ci, ii) => ci + '-' + ii;
  const toggle = key => {
    setChecked(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  let totalItems = 0;
  if (list) {
    for (let c = 0; c < list.categories.length; c++) totalItems += list.categories[c].items.length;
  }
  const checkedCount = Object.keys(checked).length;
  const pct = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const generate = () => {
    if (!list) return;
    const perCategory = list.categories.map((cat, ci) => {
      let c = 0;
      for (let ii = 0; ii < cat.items.length; ii++) if (checked[keyFor(ci, ii)]) c++;
      return { name: cat.name, checked: c, total: cat.items.length };
    });
    setSummary({
      title: list.title,
      date: new Date().toLocaleDateString(),
      tech: tech.trim() || 'N/A',
      checkedCount: checkedCount,
      total: totalItems,
      pct: pct,
      perCategory: perCategory,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Repair Type</label>
          <select style={t.formSelect} value={repairType} onChange={e => changeType(e.target.value)}>
            <option value="">Select repair type...</option>
            {REPAIR_TYPES.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
          </select>
        </div>

        {list ? (
          <div>
            <div style={t.qcProgressWrap}>
              <div style={t.qcProgressTrack}>
                <div style={{ ...t.qcProgressFill, width: pct + '%' }}>{pct + '%'}</div>
              </div>
              <div style={t.qcProgressLabel}>{checkedCount + ' of ' + totalItems + ' steps'}</div>
            </div>

            {list.categories.map((cat, ci) => (
              <div key={ci} style={t.qcCategory}>
                <div style={t.qcCategoryTitle}>{cat.name}</div>
                {cat.items.map((item, ii) => {
                  const key = keyFor(ci, ii);
                  const on = !!checked[key];
                  return (
                    <label key={ii} style={t.qcItem} onClick={() => toggle(key)}>
                      <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                      <span>
                        <span style={{ ...t.qcItemText, ...(on ? { color: '#94a3b8', textDecoration: 'line-through' } : {}) }}>{item.title}</span>
                        {item.description ? <span style={t.repairDesc}>{item.description}</span> : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}

            <div style={t.formGroup}>
              <label style={t.formLabel}>Technician Name</label>
              <input type="text" style={t.formInput} value={tech} placeholder="Technician name" onChange={e => setTech(e.target.value)} />
            </div>

            <button style={t.driverBtn} onClick={generate}>Generate Checklist Summary</button>
          </div>
        ) : null}
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: '#7dd3fc', textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: '#7dd3fc' }}>{summary.title}</div>
            <p style={t.bnHeadLine}>{summary.checkedCount + ' of ' + summary.total + ' steps completed (' + summary.pct + '%)'}</p>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
          </div>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Summary</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Technician</span><span style={t.buildLineName}>{summary.tech}</span></div>
          </div>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Category Completion</div>
            {summary.perCategory.map((c, i) => (
              <div key={i} style={t.buildLine}>
                <span style={t.buildLineLabel}>{c.name}</span>
                <span style={t.buildLinePrice}>{c.checked + ' / ' + c.total}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Burn-In Tracker (technician burn-in log).
// ---------------------------------------------------------------------------
function BurnInTracker() {
  const [systemSpecs, setSystemSpecs] = useState('');
  const [duration, setDuration] = useState('24');
  const [overallStatus, setOverallStatus] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [finalNotes, setFinalNotes] = useState('');
  const [techName, setTechName] = useState('');
  const [tests, setTests] = useState([]);
  const [pick, setPick] = useState('');
  const [summary, setSummary] = useState(null);

  const addTest = () => {
    if (!pick) return;
    const prog = getBurninProgram(pick);
    if (!prog) return;
    const values = {};
    for (let i = 0; i < prog.fields.length; i++) values[prog.fields[i].key] = '';
    setTests(prev => prev.concat([{ program: pick, values: values, runTime: '', runStatus: '', notes: '' }]));
    setPick('');
  };
  const removeTest = idx => setTests(prev => prev.filter((tst, i) => i !== idx));
  const setTestField = (idx, key, value) => {
    setTests(prev => {
      const next = prev.slice();
      next[idx] = Object.assign({}, next[idx], { values: Object.assign({}, next[idx].values, { [key]: value }) });
      return next;
    });
  };
  const setTestMeta = (idx, key, value) => {
    setTests(prev => {
      const next = prev.slice();
      next[idx] = Object.assign({}, next[idx], { [key]: value });
      return next;
    });
  };

  const generate = () => {
    setSummary({
      systemSpecs: systemSpecs.trim() || 'N/A',
      duration: duration,
      startTime: startTime,
      endTime: endTime,
      overallStatus: overallStatus,
      finalNotes: finalNotes.trim(),
      techName: techName.trim() || 'N/A',
      tests: tests.slice(),
    });
  };

  const overallMeta = v => {
    if (v === 'fail') return { color: '#f87171', text: '\u2717 ISSUES FOUND' };
    if (v === 'pass_notes') return { color: '#fbbf24', text: '\u2713 PASS WITH NOTES' };
    if (v === 'pass') return { color: '#4ade80', text: '\u2713 SYSTEM STABLE' };
    return { color: '#94a3b8', text: 'No status recorded' };
  };
  const runMeta = v => {
    for (let i = 0; i < BURNIN_RUN_STATUS.length; i++) if (BURNIN_RUN_STATUS[i].value === v) return BURNIN_RUN_STATUS[i];
    return { color: '#94a3b8', label: 'Not set', mark: '' };
  };
  const fieldLabel = (progValue, key) => {
    const prog = getBurninProgram(progValue);
    if (!prog) return key;
    for (let i = 0; i < prog.fields.length; i++) if (prog.fields[i].key === key) return prog.fields[i].label;
    return key;
  };
  const progLabel = progValue => {
    const prog = getBurninProgram(progValue);
    return prog ? prog.label : progValue;
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>System Specifications</label>
          <textarea rows={2} style={t.driverTextarea} value={systemSpecs} placeholder="CPU, GPU, RAM, Storage, etc." onChange={e => setSystemSpecs(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Burn-In Duration</label>
          <select style={t.formSelect} value={duration} onChange={e => setDuration(e.target.value)}>
            {BURNIN_DURATIONS.map(d => (<option key={d.value} value={d.value}>{d.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Start Date/Time</label>
          <input type="datetime-local" style={t.formInput} value={startTime} onChange={e => setStartTime(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>End Date/Time</label>
          <input type="datetime-local" style={t.formInput} value={endTime} onChange={e => setEndTime(e.target.value)} />
        </div>

        <div style={t.stressHeader}>Test Programs</div>
        <div style={{ ...t.buildRowInline, marginTop: 10, marginBottom: 16 }}>
          <select style={t.formSelect} value={pick} onChange={e => setPick(e.target.value)}>
            <option value="">Choose a test...</option>
            {BURNIN_PROGRAMS.map(p => (<option key={p.value} value={p.value}>{p.label}</option>))}
          </select>
          <button style={t.smallAddBtn} onClick={addTest}>+ Add Test</button>
        </div>

        {tests.map((tst, idx) => {
          const prog = getBurninProgram(tst.program);
          return (
            <div key={idx} style={t.stressSection}>
              <div style={t.urHead}>
                <div style={t.stressHeader}>{prog ? prog.label : tst.program}</div>
                <button style={t.smallRemoveBtn} onClick={() => removeTest(idx)}>Remove</button>
              </div>
              {prog ? prog.fields.map(f => (
                <div key={f.key} style={t.formGroup}>
                  <label style={t.formLabel}>{f.label}</label>
                  {f.type === 'select' ? (
                    <select style={t.formSelect} value={tst.values[f.key]} onChange={e => setTestField(idx, f.key, e.target.value)}>
                      <option value="">Select...</option>
                      {f.options.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  ) : f.type === 'textarea' ? (
                    <textarea rows={2} style={t.driverTextarea} value={tst.values[f.key]} onChange={e => setTestField(idx, f.key, e.target.value)} />
                  ) : (
                    <input type={f.type} style={t.formInput} value={tst.values[f.key]} onChange={e => setTestField(idx, f.key, e.target.value)} />
                  )}
                </div>
              )) : null}
              <div style={t.formGroup}>
                <label style={t.formLabel}>Test Run Time</label>
                <input type="datetime-local" style={t.formInput} value={tst.runTime} onChange={e => setTestMeta(idx, 'runTime', e.target.value)} />
              </div>
              <div style={t.formGroup}>
                <label style={t.formLabel}>Test Status</label>
                <select style={t.formSelect} value={tst.runStatus} onChange={e => setTestMeta(idx, 'runStatus', e.target.value)}>
                  <option value="">Select...</option>
                  {BURNIN_RUN_STATUS.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
                </select>
              </div>
              <div style={t.formGroup}>
                <label style={t.formLabel}>Notes</label>
                <textarea rows={2} style={t.driverTextarea} value={tst.notes} placeholder="Test observations, issues, results" onChange={e => setTestMeta(idx, 'notes', e.target.value)} />
              </div>
            </div>
          );
        })}

        <div style={t.formGroup}>
          <label style={t.formLabel}>Overall Status</label>
          <select style={t.formSelect} value={overallStatus} onChange={e => setOverallStatus(e.target.value)}>
            <option value="">Select...</option>
            {BURNIN_OVERALL_STATUS.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Final Notes / Issues</label>
          <textarea rows={3} style={t.driverTextarea} value={finalNotes} placeholder="Summary of burn-in results, any issues encountered" onChange={e => setFinalNotes(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Technician Name</label>
          <input type="text" style={t.formInput} value={techName} placeholder="Technician name" onChange={e => setTechName(e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Burn-In Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: overallMeta(summary.overallStatus).color, textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: overallMeta(summary.overallStatus).color }}>{overallMeta(summary.overallStatus).text}</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Burn-In Summary</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>System</span><span style={t.buildLineName}>{summary.systemSpecs}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Planned Duration</span><span style={t.buildLineName}>{summary.duration + ' hours'}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Start</span><span style={t.buildLineName}>{summary.startTime || 'N/A'}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>End</span><span style={t.buildLineName}>{summary.endTime || 'N/A'}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Technician</span><span style={t.buildLineName}>{summary.techName}</span></div>
          </div>

          {summary.tests.length === 0 ? (
            <div style={t.stepsBox}><p style={t.bnHeadLine}>No test programs were logged.</p></div>
          ) : null}

          {summary.tests.map((tst, idx) => {
            const rm = runMeta(tst.runStatus);
            const prog = getBurninProgram(tst.program);
            return (
              <div key={idx} style={{ ...t.resultRow, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={t.urHead}>
                  <div style={{ ...t.resultRowTitle, marginBottom: 0, color: '#e2e8f0' }}>{progLabel(tst.program) + (tst.runTime ? '  (' + tst.runTime + ')' : '')}</div>
                  {tst.runStatus ? <span style={{ ...t.urBadge, background: rm.color }}>{rm.mark + ' ' + rm.label}</span> : null}
                </div>
                {prog ? prog.fields.map(f => (
                  tst.values[f.key] ? (<p key={f.key} style={t.resultRowLine}><strong>{f.label + ': '}</strong>{tst.values[f.key]}</p>) : null
                )) : null}
                {tst.notes ? <p style={{ ...t.resultRowLine, fontStyle: 'italic', color: '#cbd5e1' }}>{tst.notes}</p> : null}
              </div>
            );
          })}

          {summary.finalNotes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Final Notes</div>
              <p style={t.bnHeadLine}>{summary.finalNotes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Before/After Report (technician before/after summary).
//
// In-session only. The source included Customer Name and Job Number fields, a
// branded header, a "30-day warranty on all labor" service guarantee block, a
// shop footer, and a print button; none are carried across.
// ---------------------------------------------------------------------------
function BeforeAfterReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, problemBefore: '', workPerformed: '', resultsAfter: '',
    bootBefore: '', bootAfter: '', additionalMetrics: '',
  });
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const generate = () => {
    let improvement = null;
    const before = parseFloat(form.bootBefore);
    const after = parseFloat(form.bootAfter);
    if (!isNaN(before) && before > 0 && !isNaN(after)) {
      improvement = ((before - after) / before * 100).toFixed(1);
    }
    setSummary({
      date: form.date || 'N/A',
      problemBefore: form.problemBefore.trim() || 'N/A',
      workPerformed: form.workPerformed.trim() || 'N/A',
      resultsAfter: form.resultsAfter.trim() || 'N/A',
      bootBefore: form.bootBefore.trim(),
      bootAfter: form.bootAfter.trim(),
      additionalMetrics: form.additionalMetrics.trim(),
      improvement: improvement,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Service Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Issues Reported (Before)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.problemBefore} placeholder="Describe the issues reported" onChange={e => set('problemBefore', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Work Performed</label>
          <textarea rows={4} style={t.driverTextarea} value={form.workPerformed} placeholder="Describe the work carried out" onChange={e => set('workPerformed', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Results Achieved (After)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.resultsAfter} placeholder="Describe the results after service" onChange={e => set('resultsAfter', e.target.value)} />
        </div>
        <div style={t.buildRowInline}>
          <div style={{ flex: 1 }}>
            <label style={t.formLabel}>Boot Time Before (s)</label>
            <input type="number" style={t.formInput} value={form.bootBefore} placeholder="e.g. 120" onChange={e => set('bootBefore', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={t.formLabel}>Boot Time After (s)</label>
            <input type="number" style={t.formInput} value={form.bootAfter} placeholder="e.g. 25" onChange={e => set('bootAfter', e.target.value)} />
          </div>
        </div>
        <div style={{ ...t.formGroup, marginTop: 16 }}>
          <label style={t.formLabel}>Additional Metrics (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={form.additionalMetrics} placeholder="Other benchmarks or measurements" onChange={e => set('additionalMetrics', e.target.value)} />
        </div>
        <button style={t.driverBtn} onClick={generate}>Generate Before/After Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Before/After Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Service Date</span><span style={t.buildLineName}>{summary.date}</span></div>
          </div>

          <div style={t.baGrid}>
            <div style={{ ...t.baCard, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)' }}>
              <div style={{ ...t.baCardTitle, color: '#f87171' }}>{'\u2717 Before Service'}</div>
              <p style={t.baCardLabel}>Issues Reported</p>
              <p style={t.baCardBody}>{summary.problemBefore}</p>
              {summary.bootBefore ? <p style={t.baCardMeta}>{'Boot Time: ' + summary.bootBefore + ' seconds'}</p> : null}
            </div>
            <div style={{ ...t.baCard, border: '1px solid rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.08)' }}>
              <div style={{ ...t.baCardTitle, color: '#4ade80' }}>{'\u2713 After Service'}</div>
              <p style={t.baCardLabel}>Results Achieved</p>
              <p style={t.baCardBody}>{summary.resultsAfter}</p>
              {summary.bootAfter ? <p style={t.baCardMeta}>{'Boot Time: ' + summary.bootAfter + ' seconds'}</p> : null}
            </div>
          </div>

          {summary.improvement !== null ? (
            <div style={{ ...t.bnHeadBox, borderColor: '#4ade80', textAlign: 'center' }}>
              <div style={{ ...t.bnHeadTitle, color: '#4ade80' }}>Boot Time Improvement</div>
              <p style={t.bnHeadLine}>{'From ' + summary.bootBefore + 's to ' + summary.bootAfter + 's'}</p>
              <p style={t.refurbBig}>{summary.improvement + '% Faster'}</p>
            </div>
          ) : null}

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Work Performed</div>
            <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.workPerformed}</p>
          </div>

          {summary.additionalMetrics ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Performance Metrics</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.additionalMetrics}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: System Information Report (system inventory summary).
//
// In-session only. The source included a Customer Name field, a branded
// header, a hardcoded "Technician: Technogeek Computers" line, a branded
// "Report Certification" block, a shop footer, and a print button; none are
// carried across. Serial number, make/model, and date are neutral hardware
// inventory fields and are kept.
// ---------------------------------------------------------------------------
function SystemInfoReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, technician: '', makeModel: '', serial: '', cpu: '', ram: '',
    storage: '', gpu: '', os: '', software: '', network: '', notes: '',
  });
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const generate = () => {
    const softwareList = form.software.split('\n').map(s => s.trim()).filter(s => s !== '');
    setSummary({
      date: form.date || 'N/A',
      technician: form.technician.trim() || 'N/A',
      makeModel: form.makeModel.trim() || 'N/A',
      serial: form.serial.trim() || 'N/A',
      cpu: form.cpu.trim() || 'N/A',
      ram: form.ram.trim() || 'N/A',
      storage: form.storage.trim() || 'N/A',
      gpu: form.gpu.trim() || 'N/A',
      os: form.os.trim() || 'N/A',
      network: form.network.trim() || 'N/A',
      software: softwareList,
      notes: form.notes.trim(),
    });
  };

  const textFields = [
    { key: 'makeModel', label: 'Make/Model' },
    { key: 'serial', label: 'Serial Number' },
    { key: 'cpu', label: 'Processor' },
    { key: 'ram', label: 'Memory (RAM)' },
    { key: 'storage', label: 'Storage' },
    { key: 'gpu', label: 'Graphics' },
    { key: 'os', label: 'Operating System' },
    { key: 'network', label: 'Network' },
  ];

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Service Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Technician (optional)</label>
          <input type="text" style={t.formInput} value={form.technician} placeholder="Technician name" onChange={e => set('technician', e.target.value)} />
        </div>
        {textFields.map(f => (
          <div key={f.key} style={t.formGroup}>
            <label style={t.formLabel}>{f.label}</label>
            <input type="text" style={t.formInput} value={form[f.key]} placeholder={f.label} onChange={e => set(f.key, e.target.value)} />
          </div>
        ))}
        <div style={t.formGroup}>
          <label style={t.formLabel}>Installed Applications (one per line)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.software} placeholder="One application per line" onChange={e => set('software', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Notes (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={form.notes} placeholder="Any additional notes" onChange={e => set('notes', e.target.value)} />
        </div>
        <button style={t.driverBtn} onClick={generate}>Generate System Information Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>System Information Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Service Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Technician</span><span style={t.buildLineName}>{summary.technician}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Hardware Information</div>
            {textFields.map(f => (
              <div key={f.key} style={t.buildLine}>
                <span style={t.buildLineLabel}>{f.label}</span>
                <span style={t.buildLineName}>{summary[f.key]}</span>
              </div>
            ))}
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Software Information</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Operating System</span><span style={t.buildLineName}>{summary.os}</span></div>
            {summary.software.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Installed Applications</p>
                <ul style={t.detailList}>
                  {summary.software.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
                </ul>
              </div>
            ) : null}
          </div>

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Additional Notes</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Backup Verification (technician backup verification summary).
//
// In-session only. The source produced a branded "Backup Verification
// Certificate" with a Customer Name field, a hardcoded "Verified By:
// Technogeek Computers" line, a shop footer, a MASTER_HUB link, and a print
// button; none are carried across. Output is a neutral "Technician Backup
// Verification Summary" with a "Not a customer document" disclaimer. The
// restore-instructions guidance is retained as neutral technician steps.
// ---------------------------------------------------------------------------
function BackupVerification() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, checkedBy: '', location: '', serverType: '', driveType: '',
    connectionType: '', driveSize: '', dataSize: '', verification: '',
    result: '', schedule: '', notes: '',
  });
  const [folders, setFolders] = useState({});
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const setDriveType = value => {
    setForm(prev => Object.assign({}, prev, { driveType: value, driveSize: '' }));
  };

  const toggleFolder = key => {
    setFolders(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const sizes = getBackupDriveSizes(form.driveType);

  const locationLabel = value => {
    for (let i = 0; i < BACKUP_LOCATIONS.length; i++) {
      if (BACKUP_LOCATIONS[i].value === value) return BACKUP_LOCATIONS[i].label;
    }
    return 'N/A';
  };
  const serverTypeLabel = value => {
    for (let i = 0; i < BACKUP_SERVER_TYPES.length; i++) {
      if (BACKUP_SERVER_TYPES[i].value === value) return BACKUP_SERVER_TYPES[i].label;
    }
    return '';
  };
  const testLabel = value => {
    for (let i = 0; i < BACKUP_VERIFICATION_TESTS.length; i++) {
      if (BACKUP_VERIFICATION_TESTS[i].value === value) return BACKUP_VERIFICATION_TESTS[i].label;
    }
    return 'N/A';
  };

  const generate = () => {
    let locationDesc = locationLabel(form.location);
    if (form.location === 'server') {
      const st = serverTypeLabel(form.serverType);
      locationDesc = st ? 'Server - ' + st : 'Server';
    }
    if (!form.location) locationDesc = 'N/A';

    const drive = form.driveType ? form.driveType.toUpperCase() : 'N/A';
    const conn = form.connectionType || 'N/A';
    const dsize = form.driveSize || 'N/A';
    const deviceDesc = drive + ' - ' + conn + ' - ' + dsize;

    const chosenFolders = [];
    for (let i = 0; i < BACKUP_FOLDERS.length; i++) {
      if (folders[BACKUP_FOLDERS[i].key]) chosenFolders.push(BACKUP_FOLDERS[i].label);
    }

    const resultObj = getBackupResult(form.result);
    const scheduleObj = getBackupSchedule(form.schedule);

    setSummary({
      date: form.date || 'N/A',
      checkedBy: form.checkedBy.trim() || 'N/A',
      locationDesc: locationDesc,
      deviceDesc: deviceDesc,
      dataSize: form.dataSize.trim() || 'N/A',
      schedule: scheduleObj ? scheduleObj.label : 'N/A',
      folders: chosenFolders,
      test: testLabel(form.verification),
      result: resultObj,
      notes: form.notes.trim(),
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Backup Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Checked By (optional)</label>
          <input type="text" style={t.formInput} value={form.checkedBy} placeholder="Technician name" onChange={e => set('checkedBy', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Backup Location</label>
          <select style={t.formSelect} value={form.location} onChange={e => set('location', e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_LOCATIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        {form.location === 'server' ? (
          <div style={t.formGroup}>
            <label style={t.formLabel}>Server Type</label>
            <select style={t.formSelect} value={form.serverType} onChange={e => set('serverType', e.target.value)}>
              <option value="">Select...</option>
              {BACKUP_SERVER_TYPES.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </div>
        ) : null}
        <div style={t.formGroup}>
          <label style={t.formLabel}>Drive Type</label>
          <select style={t.formSelect} value={form.driveType} onChange={e => setDriveType(e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_DRIVE_TYPES.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Connection Type</label>
          <select style={t.formSelect} value={form.connectionType} onChange={e => set('connectionType', e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_CONNECTION_TYPES.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Drive Size</label>
          <select style={t.formSelect} value={form.driveSize} onChange={e => set('driveSize', e.target.value)}>
            <option value="">{form.driveType ? 'Select...' : 'Select drive type first...'}</option>
            {sizes.map(s => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Total Data Size Backed Up</label>
          <input type="text" style={t.formInput} value={form.dataSize} placeholder="e.g. 450GB" onChange={e => set('dataSize', e.target.value)} />
        </div>

        <div style={t.qcCategory}>
          <div style={t.qcCategoryTitle}>Folders Backed Up</div>
          {BACKUP_FOLDERS.map(f => {
            const on = !!folders[f.key];
            return (
              <label key={f.key} style={t.qcItem} onClick={() => toggleFolder(f.key)}>
                <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                <span style={t.qcItemText}>{f.label}</span>
              </label>
            );
          })}
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Verification Test Performed</label>
          <select style={t.formSelect} value={form.verification} onChange={e => set('verification', e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_VERIFICATION_TESTS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Verification Result</label>
          <select style={t.formSelect} value={form.result} onChange={e => set('result', e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_RESULTS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Backup Schedule Configured</label>
          <select style={t.formSelect} value={form.schedule} onChange={e => set('schedule', e.target.value)}>
            <option value="">Select...</option>
            {BACKUP_SCHEDULES.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Notes (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={form.notes} placeholder="Any issues, exclusions, or special instructions" onChange={e => set('notes', e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Backup Verification Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Backup Verification Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Backup Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Checked By</span><span style={t.buildLineName}>{summary.checkedBy}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Backup Configuration</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Backup Location</span><span style={t.buildLineName}>{summary.locationDesc}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Device Details</span><span style={t.buildLineName}>{summary.deviceDesc}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Total Data Size</span><span style={t.buildLineName}>{summary.dataSize}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Backup Schedule</span><span style={t.buildLineName}>{summary.schedule}</span></div>
          </div>

          {summary.folders.length > 0 ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Backed Up Folders</div>
              <ul style={t.detailList}>
                {summary.folders.map((f, i) => (<li key={i} style={t.detailItem}>{f}</li>))}
              </ul>
            </div>
          ) : null}

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Verification Details</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Test Performed</span><span style={t.buildLineName}>{summary.test}</span></div>
            {summary.result ? (
              <div style={t.buildLine}>
                <span style={t.buildLineLabel}>Test Result</span>
                <span style={{ ...t.buildLineName, color: summary.result.color, fontWeight: 700 }}>{summary.result.mark + ' ' + summary.result.label}</span>
              </div>
            ) : null}
          </div>

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Additional Notes</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.notes}</p>
            </div>
          ) : null}

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Restore Instructions</div>
            <ul style={t.detailList}>
              <li style={t.detailItem}>{'Connect to backup location: ' + summary.locationDesc}</li>
              <li style={t.detailItem}>Navigate to the backup folder</li>
              <li style={t.detailItem}>Copy files back to their original locations</li>
              <li style={t.detailItem}>Verify files open correctly</li>
            </ul>
            <p style={t.bnGainNote}>Recommend testing the restore process every 3 months to confirm data integrity.</p>
          </div>

          {summary.result ? (
            <div style={{ ...t.bnHeadBox, borderColor: summary.result.color, textAlign: 'center' }}>
              <div style={{ ...t.bnHeadTitle, color: summary.result.color }}>{summary.result.mark + ' ' + summary.result.headline}</div>
              <p style={t.bnHeadLine}>{'Backup verification recorded on ' + summary.date}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Customer Handover (technician handover summary).
//
// In-session only. The source produced a branded customer-facing "Service
// Completion" document with Customer Name and Job Number fields, three
// plaintext credential inputs that were rendered into the output, a Warranty
// Information field with a "Standard warranty terms apply" fallback, a
// "Thank You for Choosing Technogeek" marketing block, a shop footer, a
// MASTER_HUB link, and a "Print for Customer" button. Per locked boundary
// decisions: customer/job identity fields are dropped in favour of a
// "Prepared By / Technician" field; ALL password inputs are removed and
// replaced with safe checklist confirmations only (nothing entered,
// rendered, or stored); the warranty section is removed entirely. Output is
// a neutral "Technician Handover Summary" with a "Not a customer document"
// disclaimer.
// ---------------------------------------------------------------------------
function CustomerHandover() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, preparedBy: '', workSummary: '', instructions: '',
  });
  const [checks, setChecks] = useState({});
  const [creds, setCreds] = useState({});
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const toggle = (setMap, key) => {
    setMap(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const generate = () => {
    let checkedCount = 0;
    for (let i = 0; i < HANDOVER_CHECKLIST.length; i++) {
      if (checks[i]) checkedCount++;
    }
    const chosenCreds = [];
    for (let i = 0; i < HANDOVER_CREDENTIAL_ITEMS.length; i++) {
      if (creds[i]) chosenCreds.push(HANDOVER_CREDENTIAL_ITEMS[i]);
    }
    setSummary({
      date: form.date || 'N/A',
      preparedBy: form.preparedBy.trim() || 'N/A',
      workSummary: form.workSummary.trim() || 'N/A',
      instructions: form.instructions.trim(),
      checkedCount: checkedCount,
      totalChecks: HANDOVER_CHECKLIST.length,
      creds: chosenCreds,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Completion Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Prepared By / Technician (optional)</label>
          <input type="text" style={t.formInput} value={form.preparedBy} placeholder="Technician name" onChange={e => set('preparedBy', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Work Completed</label>
          <textarea rows={3} style={t.driverTextarea} value={form.workSummary} placeholder="Brief description of work completed" onChange={e => set('workSummary', e.target.value)} />
        </div>

        <div style={t.qcCategory}>
          <div style={t.qcCategoryTitle}>Pre-Handover Checklist</div>
          {HANDOVER_CHECKLIST.map((item, i) => {
            const on = !!checks[i];
            return (
              <label key={i} style={t.qcItem} onClick={() => toggle(setChecks, i)}>
                <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                <span style={{ ...t.qcItemText, ...(on ? { color: '#94a3b8', textDecoration: 'line-through' } : {}) }}>{item}</span>
              </label>
            );
          })}
        </div>

        <div style={t.qcCategory}>
          <div style={t.qcCategoryTitle}>Credential Handling (no passwords stored)</div>
          {HANDOVER_CREDENTIAL_ITEMS.map((item, i) => {
            const on = !!creds[i];
            return (
              <label key={i} style={t.qcItem} onClick={() => toggle(setCreds, i)}>
                <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                <span style={t.qcItemText}>{item}</span>
              </label>
            );
          })}
        </div>

        <div style={t.formGroup}>
          <label style={t.formLabel}>Owner/User Guidance (optional)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.instructions} placeholder="e.g. first boot may take longer, recommended software, backup reminders" onChange={e => set('instructions', e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Handover Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Handover Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Completion Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Prepared By</span><span style={t.buildLineName}>{summary.preparedBy}</span></div>
          </div>

          <div style={{ ...t.bnHeadBox, borderColor: '#4ade80', textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: '#4ade80' }}>Pre-Handover Checks</div>
            <p style={t.bnHeadLine}>{summary.checkedCount + ' of ' + summary.totalChecks + ' checks completed'}</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Work Completed</div>
            <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.workSummary}</p>
          </div>

          {summary.creds.length > 0 ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Credential Handling</div>
              <ul style={t.detailList}>
                {summary.creds.map((c, i) => (<li key={i} style={t.detailItem}>{c}</li>))}
              </ul>
              <p style={t.bnGainNote}>No passwords are entered, displayed, or stored in VISINVOKE.</p>
            </div>
          ) : null}

          {summary.instructions ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Owner/User Guidance</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.instructions}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Data Recovery Assessment (technician assessment).
//
// In-session only. The source produced a branded "Data Recovery Quote" with
// customer contact fields, a dollar pricing engine, an "ESTIMATED TOTAL",
// payment/diagnostic-fee terms, a sales-process funnel, a shop footer, a
// MASTER_HUB link, a print button, and a quote number. Per the locked Data &
// Security boundary, all pricing and quote behaviour is removed. Output is a
// neutral "Technician Data Recovery Assessment" (difficulty, likelihood,
// handling risk, recommended steps) with a "Not a customer document, not a
// quote" disclaimer. Urgency is descriptive turnaround context only.
// ---------------------------------------------------------------------------
function DataRecoveryAssessment() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, assessedBy: '', deviceType: '', capacity: '', model: '',
    damageLevel: '', symptoms: '', dataType: '', urgency: 'standard',
    scope: 'full', notes: '',
  });
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const labelOf = (list, value) => {
    for (let i = 0; i < list.length; i++) {
      if (list[i].value === value) return list[i].label;
    }
    return 'N/A';
  };

  const generate = () => {
    if (!form.deviceType || !form.damageLevel) {
      setSummary({ incomplete: true });
      return;
    }
    const a = assessDataRecovery({ deviceType: form.deviceType, damageLevel: form.damageLevel });
    setSummary({
      incomplete: false,
      date: form.date || 'N/A',
      assessedBy: form.assessedBy.trim() || 'N/A',
      deviceType: labelOf(DR_DEVICE_TYPES, form.deviceType),
      capacity: form.capacity.trim() || 'N/A',
      model: form.model.trim() || 'N/A',
      damageLevel: labelOf(DR_DAMAGE_LEVELS, form.damageLevel),
      symptoms: form.symptoms.trim() || 'N/A',
      dataType: form.dataType.trim() || 'N/A',
      urgency: labelOf(DR_URGENCY, form.urgency),
      scope: labelOf(DR_SCOPE, form.scope),
      notes: form.notes.trim(),
      assessment: a,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Assessment Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Assessed By (optional)</label>
          <input type="text" style={t.formInput} value={form.assessedBy} placeholder="Technician name" onChange={e => set('assessedBy', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Device Type</label>
          <select style={t.formSelect} value={form.deviceType} onChange={e => set('deviceType', e.target.value)}>
            <option value="">Select...</option>
            {DR_DEVICE_TYPES.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Storage Capacity</label>
          <input type="text" style={t.formInput} value={form.capacity} placeholder="e.g. 1TB, 500GB, 2TB" onChange={e => set('capacity', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Manufacturer / Model</label>
          <input type="text" style={t.formInput} value={form.model} placeholder="e.g. Western Digital WD10EZEX" onChange={e => set('model', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Damage Level</label>
          <select style={t.formSelect} value={form.damageLevel} onChange={e => set('damageLevel', e.target.value)}>
            <option value="">Select...</option>
            {DR_DAMAGE_LEVELS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Symptoms</label>
          <textarea rows={3} style={t.driverTextarea} value={form.symptoms} placeholder="Describe what happened and current symptoms" onChange={e => set('symptoms', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Data Type / Importance</label>
          <textarea rows={2} style={t.driverTextarea} value={form.dataType} placeholder="e.g. family photos, business documents, databases" onChange={e => set('dataType', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Urgency / Turnaround</label>
          <select style={t.formSelect} value={form.urgency} onChange={e => set('urgency', e.target.value)}>
            {DR_URGENCY.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Recovery Scope</label>
          <select style={t.formSelect} value={form.scope} onChange={e => set('scope', e.target.value)}>
            {DR_SCOPE.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Notes (optional)</label>
          <textarea rows={3} style={t.driverTextarea} value={form.notes} placeholder="Any special considerations" onChange={e => set('notes', e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Data Recovery Assessment</button>
      </div>

      {summary && summary.incomplete ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>More detail needed</div>
            <p style={t.bnHeadLine}>Select a Device Type and a Damage Level to generate the assessment.</p>
          </div>
        </div>
      ) : null}

      {summary && !summary.incomplete ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Data Recovery Assessment</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document, and not a quote or a guarantee of recovery.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Assessment Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Assessed By</span><span style={t.buildLineName}>{summary.assessedBy}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Device Details</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Device Type</span><span style={t.buildLineName}>{summary.deviceType}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Capacity</span><span style={t.buildLineName}>{summary.capacity}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Model</span><span style={t.buildLineName}>{summary.model}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Damage Assessment</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Damage Level</span><span style={t.buildLineName}>{summary.damageLevel}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Data Importance</span><span style={t.buildLineName}>{summary.dataType}</span></div>
            <div style={{ marginTop: 8 }}>
              <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Symptoms</p>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.symptoms}</p>
            </div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Service Context</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Urgency / Turnaround</span><span style={t.buildLineName}>{summary.urgency}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Recovery Scope</span><span style={t.buildLineName}>{summary.scope}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Assessment Result</div>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Recovery Difficulty</span>
              <span style={{ ...t.buildLineName, color: summary.assessment.difficultyColor, fontWeight: 700 }}>{summary.assessment.difficulty}</span>
            </div>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Recovery Likelihood</span>
              <span style={{ ...t.buildLineName, color: summary.assessment.likelihoodColor, fontWeight: 700 }}>{summary.assessment.likelihood}</span>
            </div>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Handling Risk</span>
              <span style={{ ...t.buildLineName, color: summary.assessment.riskColor, fontWeight: 700 }}>{summary.assessment.risk}</span>
            </div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Recommended Next Steps</div>
            <ul style={t.detailList}>
              {summary.assessment.steps.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
            </ul>
          </div>

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Additional Notes</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.notes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Security Audit (technician security audit summary).
//
// In-session only. The source produced a branded "Security Audit Report"
// with a Customer/Company Name field, a shop footer, a MASTER_HUB link, a
// print button, and an Audit #SEC-xxxxxx number; none are carried across.
// Output is a neutral "Technician Security Audit Summary" with a "Not a
// customer document" disclaimer. The source hardcoded a 34-check total while
// defining 29 checkboxes; the total is computed dynamically (29), fixing the
// defect. Checklist items are yes/no controls; no passwords are entered,
// displayed, or stored.
// ---------------------------------------------------------------------------
function SecurityAudit() {
  const today = new Date().toISOString().slice(0, 10);
  const total = secTotalItems();
  const [checked, setChecked] = useState({});
  const [form, setForm] = useState({
    date: today, system: '', auditor: '', vulnerabilities: '', recommendations: '',
  });
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const keyFor = (catId, i) => catId + '-' + i;
  const toggle = key => {
    setChecked(prev => {
      const next = Object.assign({}, prev);
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const passed = Object.keys(checked).length;
  const live = secRiskLevel(passed, total);

  const generate = () => {
    const perCategory = SEC_CATEGORIES.map(cat => {
      let c = 0;
      for (let i = 0; i < cat.items.length; i++) if (checked[keyFor(cat.id, i)]) c++;
      return { title: cat.title, icon: cat.icon, checked: c, total: cat.items.length };
    });
    const r = secRiskLevel(passed, total);
    setSummary({
      date: form.date || 'N/A',
      system: form.system.trim() || 'N/A',
      auditor: form.auditor.trim() || 'N/A',
      vulnerabilities: form.vulnerabilities.trim(),
      recommendations: form.recommendations.trim(),
      passed: passed,
      total: total,
      failed: total - passed,
      risk: r,
      perCategory: perCategory,
    });
  };

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Audit Date</label>
          <input type="date" style={t.formInput} value={form.date} onChange={e => set('date', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>System / Network Description (optional)</label>
          <textarea rows={2} style={t.driverTextarea} value={form.system} placeholder="Brief description of the system being audited" onChange={e => set('system', e.target.value)} />
        </div>

        <div style={t.qcProgressWrap}>
          <div style={t.qcProgressTrack}>
            <div style={{ ...t.qcProgressFill, width: live.pct + '%', background: live.color }}>{live.pct + '%'}</div>
          </div>
          <div style={t.qcProgressLabel}>{passed + ' of ' + total + ' checks passed \u00b7 ' + live.level}</div>
        </div>

        {SEC_CATEGORIES.map(cat => (
          <div key={cat.id} style={t.qcCategory}>
            <div style={t.qcCategoryTitle}>{cat.icon + '  ' + cat.title}</div>
            {cat.items.map((item, i) => {
              const key = keyFor(cat.id, i);
              const on = !!checked[key];
              return (
                <label key={i} style={t.qcItem} onClick={() => toggle(key)}>
                  <span style={{ ...t.qcBox, ...(on ? t.qcBoxOn : {}) }}>{on ? '\u2713' : ''}</span>
                  <span style={{ ...t.qcItemText, ...(on ? { color: '#94a3b8', textDecoration: 'line-through' } : {}) }}>{item}</span>
                </label>
              );
            })}
          </div>
        ))}

        <div style={t.formGroup}>
          <label style={t.formLabel}>Identified Vulnerabilities (optional)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.vulnerabilities} placeholder="List any security issues found during the audit" onChange={e => set('vulnerabilities', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Recommendations (optional)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.recommendations} placeholder="Security improvements recommended" onChange={e => set('recommendations', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Auditor / Technician (optional)</label>
          <input type="text" style={t.formInput} value={form.auditor} placeholder="Technician name" onChange={e => set('auditor', e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Security Audit Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={{ ...t.bnHeadBox, borderColor: summary.risk.color, textAlign: 'center' }}>
            <div style={{ ...t.bnHeadTitle, color: summary.risk.color }}>{'SECURITY LEVEL: ' + summary.risk.level}</div>
            <p style={t.bnHeadLine}>{summary.passed + ' of ' + summary.total + ' checks passed (' + summary.risk.pct + '%)'}</p>
            <p style={t.bnHeadLine}>{summary.failed + ' item(s) not passed'}</p>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Audit Summary</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Audit Date</span><span style={t.buildLineName}>{summary.date}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>System</span><span style={t.buildLineName}>{summary.system}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Auditor</span><span style={t.buildLineName}>{summary.auditor}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Category Breakdown</div>
            {summary.perCategory.map((c, i) => (
              <div key={i} style={t.buildLine}>
                <span style={t.buildLineLabel}>{c.icon + '  ' + c.title}</span>
                <span style={t.buildLinePrice}>{c.checked + ' / ' + c.total}</span>
              </div>
            ))}
          </div>

          {summary.vulnerabilities ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Identified Vulnerabilities</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.vulnerabilities}</p>
            </div>
          ) : null}

          {summary.recommendations ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>Recommendations</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.recommendations}</p>
            </div>
          ) : null}

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Next Steps</div>
            <ul style={t.detailList}>
              {SEC_NEXT_STEPS.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Password Policy Generator (technician policy guidance).
//
// In-session only. The source produced a branded, customer-named
// "PASSWORD SECURITY POLICY" document with a Company/Organization Name field,
// an HR/legal compliance section, a shop footer, a MASTER_HUB link, and a
// print button. The source has no real password input and no password
// generator. Per the locked boundary: the named-company field is dropped
// (policy is generic), the HR/legal wording is replaced with neutral
// technician remediation guidance, and output is a "Technician Password
// Policy Summary". No password is entered, displayed, or stored; the tool
// produces policy guidance only, never credentials.
// ---------------------------------------------------------------------------
function PasswordPolicyGenerator() {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    effectiveDate: today, version: '1.0', preparedBy: '',
    minLength: '12', complexity: 'standard', expiration: '90', history: '5',
    lockoutThreshold: '5', lockoutDuration: '30', mfa: 'admin',
    sessionTimeout: '15', storagePolicy: 'no_writing',
    prohibited: 'company, password, admin, welcome, 123456', notes: '',
  });
  const [summary, setSummary] = useState(null);

  const set = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }));

  const generate = () => {
    const policy = buildPasswordPolicy({
      minLength: form.minLength, complexity: form.complexity,
      expiration: form.expiration, history: form.history,
      lockoutThreshold: form.lockoutThreshold, lockoutDuration: form.lockoutDuration,
      mfa: form.mfa, sessionTimeout: form.sessionTimeout, storagePolicy: form.storagePolicy,
    });
    const prohibitedList = form.prohibited.split(',').map(s => s.trim()).filter(s => s !== '');
    setSummary({
      effectiveDate: form.effectiveDate || 'N/A',
      version: form.version.trim() || '1.0',
      preparedBy: form.preparedBy.trim() || 'N/A',
      prohibitedList: prohibitedList,
      notes: form.notes.trim(),
      policy: policy,
    });
  };

  const selectField = (label, key, options) => (
    <div style={t.formGroup}>
      <label style={t.formLabel}>{label}</label>
      <select style={t.formSelect} value={form[key]} onChange={e => set(key, e.target.value)}>
        {options.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );

  return (
    <div>
      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Policy Effective Date (optional)</label>
          <input type="date" style={t.formInput} value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Policy Version (optional)</label>
          <input type="text" style={t.formInput} value={form.version} placeholder="e.g. 1.0" onChange={e => set('version', e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Prepared By / Technician (optional)</label>
          <input type="text" style={t.formInput} value={form.preparedBy} placeholder="Technician name" onChange={e => set('preparedBy', e.target.value)} />
        </div>

        {selectField('Minimum Password Length', 'minLength', PP_MIN_LENGTHS)}
        {selectField('Password Complexity', 'complexity', PP_COMPLEXITY)}
        {selectField('Password Expiration', 'expiration', PP_EXPIRATION)}
        {selectField('Password History', 'history', PP_HISTORY)}
        {selectField('Account Lockout Threshold', 'lockoutThreshold', PP_LOCKOUT_THRESHOLD)}
        {selectField('Lockout Duration', 'lockoutDuration', PP_LOCKOUT_DURATION)}
        {selectField('Multi-Factor Authentication (MFA)', 'mfa', PP_MFA)}
        {selectField('Session Timeout', 'sessionTimeout', PP_SESSION_TIMEOUT)}

        <div style={t.formGroup}>
          <label style={t.formLabel}>Prohibited Password Examples (comma-separated)</label>
          <textarea rows={2} style={t.driverTextarea} value={form.prohibited} placeholder="e.g. company name, common words, Password123" onChange={e => set('prohibited', e.target.value)} />
        </div>
        {selectField('Password Storage Policy', 'storagePolicy', PP_STORAGE_POLICY)}
        <div style={t.formGroup}>
          <label style={t.formLabel}>Additional Policy Notes (optional)</label>
          <textarea rows={4} style={t.driverTextarea} value={form.notes} placeholder="Any special requirements or exceptions" onChange={e => set('notes', e.target.value)} />
        </div>

        <button style={t.driverBtn} onClick={generate}>Generate Password Policy Summary</button>
      </div>

      {summary ? (
        <div style={t.driverResult}>
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Password Policy Summary</div>
            <p style={t.bnGainNote}>For technician reference. Not a customer document.</p>
            <p style={t.bnGainNote}>No passwords are entered, displayed, or stored in VISINVOKE. Use this tool to create policy guidance, not credentials.</p>
            <p style={t.bnGainNote}>Credentials should be handled separately through an approved password manager or owner-controlled process.</p>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Policy Version</span><span style={t.buildLineName}>{summary.version}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Effective Date</span><span style={t.buildLineName}>{summary.effectiveDate}</span></div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Prepared By</span><span style={t.buildLineName}>{summary.preparedBy}</span></div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>1. Purpose</div>
            <p style={t.bnHeadLine}>This guidance sets out recommended password requirements for the organisation's systems and accounts. It is intended for technician reference when drafting or reviewing a password policy.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>2. Password Creation Requirements</div>
            <div style={t.buildLine}><span style={t.buildLineLabel}>Minimum Length</span><span style={t.buildLineName}>{summary.policy.minLength + ' characters'}</span></div>
            <div style={{ marginTop: 8 }}>
              <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Complexity</p>
              <p style={t.bnHeadLine}>{summary.policy.complexityDesc}</p>
            </div>
            <div style={{ marginTop: 8 }}>
              <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Prohibited Passwords</p>
              <ul style={t.detailList}>
                {summary.prohibitedList.length > 0 ? (
                  <li style={t.detailItem}>{'Any password containing: ' + summary.prohibitedList.join(', ')}</li>
                ) : null}
                {PP_PROHIBITED_TYPES.map((p, i) => (<li key={i} style={t.detailItem}>{p}</li>))}
              </ul>
            </div>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>3. Password Management</div>
            <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Expiration</p>
            <p style={t.bnHeadLine}>{summary.policy.expirationText}</p>
            <p style={{ ...t.buildLineLabel, margin: '10px 0 6px 0' }}>History</p>
            <p style={t.bnHeadLine}>{summary.policy.historyText}</p>
            <p style={{ ...t.buildLineLabel, margin: '10px 0 6px 0' }}>Initial Password</p>
            <p style={t.bnHeadLine}>Temporary or initial passwords should be changed immediately at first login.</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>4. Account Security</div>
            <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Account Lockout</p>
            <p style={t.bnHeadLine}>{summary.policy.lockoutText}</p>
            <div style={{ ...t.buildLine, marginTop: 10 }}><span style={t.buildLineLabel}>Multi-Factor Authentication</span><span style={t.buildLineName}>{summary.policy.mfaLabel}</span></div>
            <p style={{ ...t.buildLineLabel, margin: '10px 0 6px 0' }}>Session Timeout</p>
            <p style={t.bnHeadLine}>{summary.policy.sessionText}</p>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>5. Password Protection</div>
            <p style={{ ...t.buildLineLabel, marginBottom: 6 }}>Storage</p>
            <p style={t.bnHeadLine}>{summary.policy.storageLabel}</p>
            <p style={{ ...t.buildLineLabel, margin: '10px 0 6px 0' }}>Sharing</p>
            <p style={t.bnHeadLine}>Passwords should never be shared with anyone, including IT staff or colleagues. Each user is responsible for activity under their own account.</p>
            <p style={{ ...t.buildLineLabel, margin: '10px 0 6px 0' }}>Suspected Compromise</p>
            <ul style={t.detailList}>
              {PP_COMPROMISE_STEPS.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
            </ul>
          </div>

          {summary.notes ? (
            <div style={t.stepsBox}>
              <div style={t.stepsBoxTitle}>6. Additional Requirements</div>
              <p style={{ ...t.bnHeadLine, whiteSpace: 'pre-line' }}>{summary.notes}</p>
            </div>
          ) : null}

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Ongoing Compliance</div>
            <ul style={t.detailList}>
              {PP_COMPLIANCE_NOTES.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
            </ul>
          </div>

          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Best Practices</div>
            <ul style={t.detailList}>
              {PP_BEST_PRACTICES.map((s, i) => (<li key={i} style={t.detailItem}>{s}</li>))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Technician Tool Index (reference-only external tool list).
//
// In-session only. The source was a USB Tech Tools Launcher: a drive-letter
// input, per-tool buttons that built local paths and a "start" command copied
// to the clipboard / a .bat file, and instructions to run tools from a USB
// folder. ALL launcher, USB, local-path, batch-file, .exe filename, and
// clipboard behaviour is removed. This renders a searchable, filterable
// reference index of tool name / category / purpose / status only. No launch,
// run, download, install, copy-to-USB, file path, or vendor binary. No links
// are shown (the source had none, and none are invented). Search and category
// filter are in-session React state.
// ---------------------------------------------------------------------------
function TechnicianToolIndex() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const term = search.trim().toLowerCase();

  const matches = tool => {
    if (term === '') return true;
    if (tool.name.toLowerCase().indexOf(term) !== -1) return true;
    if (tool.note.toLowerCase().indexOf(term) !== -1) return true;
    return false;
  };

  const visibleCategories = TTI_CATEGORIES
    .filter(cat => category === 'all' || cat.id === category)
    .map(cat => {
      const tools = cat.tools.filter(matches);
      return { cat: cat, tools: tools };
    })
    .filter(group => group.tools.length > 0);

  const totalVisible = visibleCategories.reduce((sum, g) => sum + g.tools.length, 0);

  return (
    <div>
      <div style={{ ...t.bnHeadBox, borderColor: '#38bdf8' }}>
        <div style={{ ...t.bnHeadTitle, color: '#38bdf8' }}>Reference Only</div>
        <p style={t.bnHeadLine}>VISINVOKE does not host, bundle, download, launch, or execute any third-party software. This is a reference index only. Obtain each tool from its official vendor source and confirm licensing before use.</p>
      </div>

      <div style={t.formCard}>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Search Tools</label>
          <input type="text" style={t.formInput} value={search} placeholder="Search by name or purpose" onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={t.formGroup}>
          <label style={t.formLabel}>Category</label>
          <select style={t.formSelect} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="all">All categories</option>
            {TTI_CATEGORIES.map(cat => (<option key={cat.id} value={cat.id}>{cat.title}</option>))}
          </select>
        </div>
        <p style={t.bnGainNote}>{'Showing ' + totalVisible + ' of ' + ttiToolCount() + ' reference tools.'}</p>
      </div>

      {visibleCategories.length === 0 ? (
        <div style={t.statePanel}>
          <div style={t.stateIcon}>{'\u{1F50D}'}</div>
          <div style={t.stateTitle}>No tools match</div>
          <p style={t.stateText}>Try a different search term or category.</p>
        </div>
      ) : null}

      {visibleCategories.map(group => (
        <div key={group.cat.id} style={t.qcCategory}>
          <div style={t.qcCategoryTitle}>{group.cat.icon + '  ' + group.cat.title}</div>
          {group.tools.map((tool, i) => {
            const st = getTtiStatus(tool.status);
            return (
              <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ ...t.buildLineName, fontWeight: 700 }}>{tool.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: st.color, border: '1px solid ' + st.color, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}>{st.label}</span>
                </div>
                <p style={{ ...t.buildLineLabel, marginTop: 4 }}>{tool.note}</p>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Quick Start (VISINVOKE Tech Hub quick-start guide).
//
// Rewritten from a standalone-package quick start (extract files, open
// MASTER_HUB.html, bookmark, work offline, USB tool launcher, print). None of
// that is preserved. Static in-session content only.
// ---------------------------------------------------------------------------
function QuickStartGuide() {
  return (
    <div style={t.driverResult}>
      <div style={{ ...t.bnHeadBox, borderColor: '#38bdf8' }}>
        <div style={{ ...t.bnHeadTitle, color: '#38bdf8' }}>Quick Start</div>
        <p style={t.bnHeadLine}>Tech Hub runs inside VISINVOKE. Sign in, unlock Technician Access, and use the tools in-session.</p>
      </div>

      {QUICK_START_STEPS.map((step, i) => (
        <div key={i} style={t.stepsBox}>
          <div style={t.stepsBoxTitle}>{(i + 1) + '. ' + step.title}</div>
          <p style={t.bnHeadLine}>{step.text}</p>
        </div>
      ))}

      <div style={t.stepsBox}>
        <div style={t.stepsBoxTitle}>Good to know</div>
        <ul style={t.detailList}>
          {QUICK_START_NOTES.map((n, i) => (<li key={i} style={t.detailItem}>{n}</li>))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: User Guide (VISINVOKE Tech Hub technician guide).
//
// Rewritten from a standalone-package user guide (extract/bookmark/offline,
// USB launcher, printable customer reports, Service Package Builder / Contract
// Calculator, shop branding, stale tool counts). None preserved. Explains the
// six current groups and the in-session / no-password / reference-only /
// sign-out boundaries. Static content only.
// ---------------------------------------------------------------------------
function UserGuide() {
  return (
    <div style={t.driverResult}>
      <div style={{ ...t.bnHeadBox, borderColor: '#38bdf8' }}>
        <div style={{ ...t.bnHeadTitle, color: '#38bdf8' }}>User Guide</div>
        <p style={t.bnHeadLine}>Tech Hub groups a set of technician utilities inside VISINVOKE. Open it from Utilities once your account has Technician Access.</p>
      </div>

      <div style={t.stepsBox}>
        <div style={t.stepsBoxTitle}>The six tool groups</div>
        {USER_GUIDE_GROUPS.map((g, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <p style={{ ...t.buildLineName, fontWeight: 700 }}>{g.title}</p>
            <p style={{ ...t.buildLineLabel, marginTop: 2 }}>{g.text}</p>
          </div>
        ))}
      </div>

      <div style={t.stepsBox}>
        <div style={t.stepsBoxTitle}>How Tech Hub works</div>
        <ul style={t.detailList}>
          {USER_GUIDE_NOTES.map((n, i) => (<li key={i} style={t.detailItem}>{n}</li>))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Converted tool: Tech Hub Help (VISINVOKE Tech Hub help and safety page).
//
// Rewritten from README.html (standalone setup, system requirements, USB
// launcher structure with X:\\USB Tech\\ and .bat, admin privileges, print,
// shop branding, license block). None preserved. General help plus safe-usage
// notes. Static content only.
// ---------------------------------------------------------------------------
function TechHubHelp() {
  return (
    <div style={t.driverResult}>
      <div style={{ ...t.bnHeadBox, borderColor: '#38bdf8' }}>
        <div style={{ ...t.bnHeadTitle, color: '#38bdf8' }}>Tech Hub Help</div>
        <p style={t.bnHeadLine}>Help and safe-usage notes for the VISINVOKE Tech Hub.</p>
      </div>

      {TECH_HUB_HELP_SECTIONS.map((s, i) => (
        <div key={i} style={t.stepsBox}>
          <div style={t.stepsBoxTitle}>{s.title}</div>
          <p style={t.bnHeadLine}>{s.text}</p>
        </div>
      ))}

      <div style={t.stepsBox}>
        <div style={t.stepsBoxTitle}>Safe-usage notes</div>
        <ul style={t.detailList}>
          {TECH_HUB_HELP_SAFETY.map((n, i) => (<li key={i} style={t.detailItem}>{n}</li>))}
        </ul>
        <p style={t.bnGainNote}>For technician reference. Tools run in-session inside VISINVOKE.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Launcher-state resource detail (correction stage).
//
// Tech Utilities are launcher-run resources through the VISINVOKE Desktop App.
// Nothing runs in the browser. This detail page shows resource metadata plus
// the viewer's launcher-action state. It never executes a utility, never
// downloads a file, never touches the file system, and never references a
// Desktop App bridge that does not exist yet.
//
// Normal signed-in users see "Technician Access Required" plus request-access
// wording. Technician Access users see the Desktop App launcher state (a
// disabled/informational control, since no Desktop App bridge exists yet).
// ---------------------------------------------------------------------------
function ResourceDetail({ tool, group, hasAccess, onBack }) {
  const state = getLauncherState(tool, hasAccess);
  const groupTitle = group && group.title ? group.title : 'Tech Utilities';

  const rows = [
    { label: 'Group', value: groupTitle },
    { label: 'Resource Type', value: tool.resourceType || 'technician-resource' },
    { label: 'Access Requirement', value: 'Technician Access' },
    { label: 'Availability', value: tool.availability || 'available-to-technicians' },
    { label: 'Licence Status', value: tool.licenceStatus || 'VISINVOKE-owned' },
    { label: 'Launcher Action', value: tool.launcherAction || 'run-through-desktop-launcher' },
  ];

  return (
    <div>
      <div style={t.toolHeader}>
        <span style={t.toolHeaderIcon}>{tool.icon}</span>
        <div>
          <h1 style={t.toolHeaderTitle}>{tool.title}</h1>
          <p style={t.toolHeaderDesc}>{tool.description}</p>
        </div>
      </div>

      <div style={t.driverResult}>
        <div style={{ ...t.bnHeadBox, borderColor: state.color, textAlign: 'center' }}>
          <div style={{ ...t.bnHeadTitle, color: state.color }}>{state.label}</div>
          <p style={t.bnHeadLine}>
            {hasAccess
              ? 'This resource runs through the VISINVOKE Desktop App launcher on the current PC. It does not run in this browser page.'
              : 'You can view this resource here. Running it requires Technician Access through the VISINVOKE Desktop App.'}
          </p>
        </div>

        <div style={t.stepsBox}>
          <div style={t.stepsBoxTitle}>Resource Details</div>
          {rows.map((r, i) => (
            <div key={i} style={t.buildLine}>
              <span style={t.buildLineLabel}>{r.label}</span>
              <span style={t.buildLineName}>{r.value}</span>
            </div>
          ))}
        </div>

        {hasAccess ? (
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Launcher Action</div>
            <div style={t.buildLine}>
              <span style={t.buildLineLabel}>Desktop App</span>
              <span style={{ ...t.buildLineName, color: state.color, fontWeight: 700 }}>{state.sub}</span>
            </div>
            <button style={{ ...t.stateBtn, opacity: 0.6, cursor: 'not-allowed', marginTop: 12 }} disabled>
              {state.label}
            </button>
            <p style={t.bnGainNote}>Desktop App launcher integration pending. This page does not run the utility in the browser.</p>
          </div>
        ) : (
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Technician Access Required</div>
            <p style={t.bnHeadLine}>
              Technician utilities run through the VISINVOKE Desktop App and require Technician Access. You can browse the
              full resource catalogue, but running utilities is limited to approved technician accounts.
            </p>
            <p style={t.bnGainNote}>To request Technician Access, contact VISINVOKE Support.</p>
          </div>
        )}

        {tool.userFacingNotes ? (
          <div style={t.stepsBox}>
            <div style={t.stepsBoxTitle}>Notes</div>
            <p style={t.bnHeadLine}>{tool.userFacingNotes}</p>
            {tool.safetyNotes ? <p style={t.bnGainNote}>{tool.safetyNotes}</p> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Host.
// ---------------------------------------------------------------------------
//
// CORRECTION STAGE NOTICE: the renderConvertedTool switch and every converted
// React component below are PRESERVED but NOT invoked by the live route. Tech
// Utilities are launcher-run resources through the VISINVOKE Desktop App, so
// the host renders ResourceDetail (launcher-state metadata) instead of calling
// renderConvertedTool. The dead components are kept intact for reference and
// any future decision; they must not be wired back into the live route without
// an approved change of model.
function renderConvertedTool(slug) {
  if (slug === 'common-fixes-reference') return <CommonFixesReference />;
  if (slug === 'problem-diagnostic-tree') return <ProblemDiagnosticTree />;
  if (slug === 'error-code-lookup') return <ErrorCodeLookup />;
  if (slug === 'driver-issue-resolver') return <DriverIssueResolver />;
  if (slug === 'hardware-compatibility-checker') return <HardwareCompatibilityChecker />;
  if (slug === 'system-bottleneck-analyzer') return <SystemBottleneckAnalyzer />;
  if (slug === 'pc-build-configurator') return <PCBuildConfigurator />;
  if (slug === 'gaming-pc-calculator') return <GamingPCCalculator />;
  if (slug === 'workstation-builder') return <WorkstationBuilder />;
  if (slug === 'upgrade-recommendation') return <UpgradeRecommendation />;
  if (slug === 'build-spec-sheet') return <BuildSpecSheet />;
  if (slug === 'refurbished-pricer') return <RefurbishedPricer />;
  if (slug === 'stress-test-protocol') return <StressTestProtocol />;
  if (slug === 'qc-checklist') return <QCChecklist />;
  if (slug === 'repair-checklist') return <RepairChecklist />;
  if (slug === 'burnin-tracker') return <BurnInTracker />;
  if (slug === 'before-after-report') return <BeforeAfterReport />;
  if (slug === 'system-info-report') return <SystemInfoReport />;
  if (slug === 'backup-verification') return <BackupVerification />;
  if (slug === 'customer-handover') return <CustomerHandover />;
  if (slug === 'data-recovery-quote') return <DataRecoveryAssessment />;
  if (slug === 'security-audit') return <SecurityAudit />;
  if (slug === 'password-policy') return <PasswordPolicyGenerator />;
  if (slug === 'external-tool-reference') return <TechnicianToolIndex />;
  if (slug === 'quick-start') return <QuickStartGuide />;
  if (slug === 'user-guide') return <UserGuide />;
  if (slug === 'tech-hub-help') return <TechHubHelp />;
  return null;
}

export default function TechHubToolPage() {
  const { toolSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasAccess = getTechnicianAccess(user);
  const resolved = getTechHubTool(toolSlug);

  const backToHub = () => navigate(TECH_HUB_BASE_ROUTE);

  // Correction stage: the slug route is a launcher-state resource-detail page.
  // Every signed-in user can view it; Technician Access only changes the
  // launcher-action state. renderConvertedTool is intentionally NOT called -
  // Tech Utilities run through the VISINVOKE Desktop App, not in the browser.
  let inner;
  if (!resolved) {
    inner = (
      <div style={t.statePanel}>
        <div style={t.stateIcon}>{'\u{1F50D}'}</div>
        <div style={t.stateTitle}>Resource not found</div>
        <p style={t.stateText}>This Tech Utilities resource does not exist. Return to the catalogue to browse available resources.</p>
        <button style={t.stateBtn} onClick={backToHub}>Back to Tech Utilities</button>
      </div>
    );
  } else {
    inner = (
      <ResourceDetail tool={resolved.tool} group={resolved.group} hasAccess={hasAccess} onBack={backToHub} />
    );
  }

  return (
    <div style={t.root}>
      <ProductNav />
      <div style={t.body}>
        <button
          style={t.backLink}
          onClick={backToHub}
          onMouseEnter={e => { e.currentTarget.style.color = '#7dd3fc'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; }}
        >
          {'\u2190 VISINVOKE Tech Hub'}
        </button>
        {inner}
      </div>
    </div>
  );
}

const t = {
  root: {
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  body: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '24px 40px 72px',
    boxSizing: 'border-box',
    width: '100%',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#aab8c9',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 18,
    transition: 'color 180ms ease',
  },
  toolHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 26,
  },
  toolHeaderIcon: {
    fontSize: 34,
    lineHeight: 1,
    flexShrink: 0,
  },
  toolHeaderTitle: {
    fontSize: 32,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.15,
    margin: 0,
    letterSpacing: '-0.4px',
  },
  toolHeaderDesc: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: '8px 0 0',
    maxWidth: 760,
  },

  // Common Fixes
  searchWrap: {
    marginBottom: 22,
  },
  searchInput: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 16,
    color: '#e2e8f0',
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(125,211,252,0.5)',
    borderRadius: 12,
    boxSizing: 'border-box',
    outline: 'none',
  },
  fixList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fixCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderLeft: '4px solid #7dd3fc',
    borderRadius: 12,
    padding: '20px 22px',
    boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
  },
  fixTitle: {
    fontSize: 19,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 10,
  },
  timeBadge: {
    display: 'inline-block',
    background: 'rgba(125,211,252,0.16)',
    color: '#7dd3fc',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 14,
  },
  fixSteps: {
    margin: 0,
    paddingLeft: 22,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  fixStep: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
  },
  emptyNote: {
    fontSize: 15,
    color: '#94a3b8',
    padding: '20px 4px',
  },

  // Diagnostic tree
  problemGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16,
    marginTop: 18,
  },
  problemCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 14,
    padding: '26px 18px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'transform 160ms ease, border-color 160ms ease',
  },
  problemIcon: {
    fontSize: 40,
    lineHeight: 1,
  },
  problemName: {
    fontSize: 15,
    fontWeight: 800,
    color: '#ffffff',
  },
  flowTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: '0 0 8px',
  },
  backInlineBtn: {
    background: 'rgba(255,255,255,0.08)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 18,
  },
  stepCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '20px 22px',
    marginBottom: 16,
  },
  stepHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  stepNumber: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#ffffff',
  },
  stepContent: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: 0,
  },
  toolsNeeded: {
    marginTop: 14,
    fontSize: 13,
    color: '#e2e8f0',
    background: 'rgba(56,189,248,0.10)',
    border: '1px solid rgba(56,189,248,0.28)',
    borderRadius: 8,
    padding: '10px 14px',
  },
  decisionBox: {
    marginTop: 16,
    background: 'rgba(99,102,241,0.08)',
    border: '1px solid rgba(99,102,241,0.28)',
    borderRadius: 10,
    padding: '16px 18px',
  },
  decisionQuestion: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e2e8f0',
    marginBottom: 12,
  },
  decisionButtons: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  decisionBtn: {
    background: 'rgba(255,255,255,0.06)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  decisionBtnYes: {
    background: 'rgba(34,197,94,0.18)',
    borderColor: 'rgba(34,197,94,0.5)',
    color: '#4ade80',
  },
  decisionBtnNo: {
    background: 'rgba(239,68,68,0.16)',
    borderColor: 'rgba(239,68,68,0.5)',
    color: '#f87171',
  },
  solutionBox: {
    marginTop: 14,
    background: 'rgba(34,197,94,0.10)',
    border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 8,
    padding: '14px 16px',
  },
  solutionTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#4ade80',
    marginBottom: 6,
  },
  solutionText: {
    fontSize: 14,
    color: '#d1fae5',
    lineHeight: 1.6,
    margin: 0,
  },
  warningBox: {
    marginTop: 14,
    background: 'rgba(251,191,36,0.10)',
    border: '1px solid rgba(251,191,36,0.35)',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 13,
    color: '#fde68a',
    lineHeight: 1.6,
  },
  commonBox: {
    marginTop: 8,
    background: 'linear-gradient(160deg, #101a2e 0%, #1e3a8a 60%, #1d4ed8 100%)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 12,
    padding: '20px 22px',
  },
  commonTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 12,
  },
  commonList: {
    margin: 0,
    paddingLeft: 22,
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  commonItem: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.6,
  },

  // Shared states
  statePanel: {
    maxWidth: 640,
    margin: '10px auto 0',
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: '40px 34px',
    textAlign: 'center',
    boxShadow: '0 6px 24px rgba(0,0,0,0.42)',
  },
  stateIcon: {
    fontSize: 40,
    lineHeight: 1,
    marginBottom: 16,
  },
  stateTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 12,
  },
  stateText: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: '0 0 20px',
  },
  stateBtn: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    padding: '11px 22px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },

  // Error Code Lookup
  tabRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 18,
  },
  tab: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    borderRadius: 20,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  tabActive: {
    ...glassSurface('#0369A1', '#38bdf8', '#7dd3fc'),
  },
  resultCount: {
    fontSize: 15,
    fontWeight: 800,
    color: '#7dd3fc',
    marginBottom: 16,
  },
  errorCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderLeft: '4px solid #7dd3fc',
    borderRadius: 12,
    padding: '18px 20px',
    boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
    cursor: 'pointer',
  },
  errorCode: {
    fontSize: 15,
    fontWeight: 800,
    color: '#38bdf8',
    fontFamily: 'Consolas, Menlo, monospace',
    marginBottom: 4,
  },
  errorName: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 6,
  },
  errorDesc: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
  },
  beepPattern: {
    display: 'inline-block',
    marginTop: 10,
    background: 'rgba(56,189,248,0.14)',
    color: '#7dd3fc',
    padding: '4px 12px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'Consolas, Menlo, monospace',
  },
  frequency: {
    marginTop: 10,
    fontSize: 13,
    fontWeight: 700,
    color: '#7dd3fc',
  },
  errorDetails: {
    marginTop: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  causeBox: {
    background: 'rgba(251,191,36,0.10)',
    border: '1px solid rgba(251,191,36,0.32)',
    borderRadius: 8,
    padding: '12px 16px',
  },
  causeTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#fbbf24',
    marginBottom: 8,
  },
  solveBox: {
    background: 'rgba(34,197,94,0.10)',
    border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 8,
    padding: '12px 16px',
  },
  solveTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#4ade80',
    marginBottom: 8,
  },
  detailList: {
    margin: 0,
    paddingLeft: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  detailItem: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.6,
  },
  toggleText: {
    marginTop: 14,
    fontSize: 13,
    fontWeight: 700,
    color: '#38bdf8',
  },

  // Driver Issue Resolver
  driverInputSection: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '22px 24px',
    marginBottom: 22,
    boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
  },
  driverInputLabel: {
    fontSize: 18,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 8,
  },
  driverInputHint: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: '0 0 14px',
  },
  driverTextarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 14,
    color: '#e2e8f0',
    background: '#0d1117',
    border: '1px solid rgba(125,211,252,0.4)',
    borderRadius: 10,
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'Consolas, Menlo, monospace',
    marginBottom: 14,
  },
  driverBtn: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  driverResult: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  driverInvalid: {
    background: 'rgba(239,68,68,0.10)',
    border: '1px solid rgba(239,68,68,0.4)',
    borderRadius: 10,
    padding: '18px 20px',
    color: '#fca5a5',
  },
  driverInvalidTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#f87171',
    marginBottom: 8,
  },
  driverInvalidText: {
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 4px',
  },
  identBox: {
    background: 'rgba(34,197,94,0.10)',
    border: '1px solid rgba(34,197,94,0.35)',
    borderRadius: 10,
    padding: '18px 20px',
  },
  identTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: '#4ade80',
    marginBottom: 12,
  },
  identLine: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.7,
    margin: '0 0 6px',
  },
  identCode: {
    background: '#0d1117',
    color: '#7dd3fc',
    padding: '3px 8px',
    borderRadius: 4,
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: 13,
  },
  driverBox: {
    background: 'rgba(251,191,36,0.10)',
    border: '1px solid rgba(251,191,36,0.35)',
    borderRadius: 10,
    padding: '16px 20px',
  },
  driverBoxTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#fbbf24',
    marginBottom: 10,
  },
  driverBoxText: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.7,
    margin: '0 0 6px',
  },
  driverLink: {
    color: '#7dd3fc',
    fontWeight: 700,
  },
  stepsBox: {
    background: 'rgba(59,130,246,0.10)',
    border: '1px solid rgba(59,130,246,0.35)',
    borderRadius: 10,
    padding: '16px 20px',
  },
  stepsBoxTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#60a5fa',
    marginBottom: 10,
  },
  notesBox: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.32)',
    borderRadius: 10,
    padding: '16px 20px',
  },
  notesBoxTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#f87171',
    marginBottom: 10,
  },

  // Compatibility Checker + Bottleneck Analyzer (shared form)
  formCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '22px 24px',
    marginBottom: 22,
    boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    color: '#cbd5e1',
    marginBottom: 6,
  },
  formSelect: {
    width: '100%',
    padding: '11px 12px',
    fontSize: 14,
    color: '#e2e8f0',
    background: '#0d1117',
    border: '1px solid rgba(125,211,252,0.4)',
    borderRadius: 8,
    boxSizing: 'border-box',
    outline: 'none',
  },
  formInput: {
    width: '100%',
    padding: '11px 12px',
    fontSize: 14,
    color: '#e2e8f0',
    background: '#0d1117',
    border: '1px solid rgba(125,211,252,0.4)',
    borderRadius: 8,
    boxSizing: 'border-box',
    outline: 'none',
  },
  resultRow: {
    borderRadius: 10,
    padding: '14px 18px',
  },
  resultRowTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 6,
  },
  resultRowLine: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.6,
    margin: '0 0 4px',
  },

  // Bottleneck analyzer specific
  bnHeadBox: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '2px solid #fbbf24',
    borderRadius: 12,
    padding: '20px 24px',
  },
  bnHeadTitle: {
    fontSize: 20,
    fontWeight: 800,
    marginBottom: 12,
  },
  bnHeadLine: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.7,
    margin: '0 0 6px',
  },
  compGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
  },
  compCard: {
    borderRadius: 10,
    padding: '16px 18px',
  },
  compCardLabel: {
    fontSize: 13,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  compCardValue: {
    fontSize: 16,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 6,
    lineHeight: 1.3,
  },
  compCardSub: {
    fontSize: 13,
    color: '#cbd5e1',
    lineHeight: 1.5,
  },
  bnGainBox: {
    background: 'rgba(59,130,246,0.10)',
    border: '1px solid rgba(59,130,246,0.35)',
    borderRadius: 10,
    padding: '16px 20px',
  },
  bnGainTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#60a5fa',
    marginBottom: 10,
  },
  bnGainNote: {
    fontSize: 13,
    color: '#94a3b8',
    lineHeight: 1.6,
    margin: '10px 0 0',
  },

  // PC Build Configurator
  csvRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
    marginBottom: 20,
    paddingBottom: 18,
    borderBottom: '1px solid rgba(255,255,255,0.10)',
  },
  csvBtn: {
    background: 'rgba(56,189,248,0.14)',
    color: '#7dd3fc',
    border: '1px solid rgba(56,189,248,0.4)',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  csvNote: {
    fontSize: 13,
    color: '#94a3b8',
    lineHeight: 1.5,
  },
  buildRowInline: {
    display: 'flex',
    gap: 10,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  buildBtnRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  resetBtn: {
    background: 'rgba(255,255,255,0.08)',
    color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 10,
    padding: '12px 22px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  buildLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
  },
  buildLineLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#aab8c9',
    minWidth: 150,
    flexShrink: 0,
  },
  buildLineName: {
    fontSize: 14,
    color: '#e2e8f0',
    flex: 1,
  },
  buildLinePrice: {
    fontSize: 14,
    fontWeight: 700,
    color: '#e2e8f0',
    marginLeft: 'auto',
    flexShrink: 0,
  },

  // Upgrade Recommendation
  urHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  urBadge: {
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.06em',
    flexShrink: 0,
  },

  // Build Spec Sheet
  smallAddBtn: {
    background: 'rgba(56,189,248,0.14)',
    color: '#7dd3fc',
    border: '1px solid rgba(56,189,248,0.4)',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  smallRemoveBtn: {
    background: 'rgba(239,68,68,0.12)',
    color: '#f87171',
    border: '1px solid rgba(239,68,68,0.35)',
    borderRadius: 8,
    padding: '9px 14px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  customItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 8,
    padding: '8px 12px',
    marginBottom: 8,
  },
  customItemText: {
    fontSize: 14,
    color: '#e2e8f0',
  },

  // Refurbished PC Valuation
  refurbBig: {
    fontSize: 40,
    fontWeight: 900,
    color: '#4ade80',
    lineHeight: 1.1,
    margin: '10px 0',
  },

  // Before/After Report
  baGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 14,
    margin: '14px 0',
  },
  baCard: {
    borderRadius: 12,
    padding: 18,
  },
  baCardTitle: {
    fontSize: 15,
    fontWeight: 800,
    textAlign: 'center',
    marginBottom: 12,
  },
  baCardLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.65)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  baCardBody: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.92)',
    lineHeight: 1.5,
    whiteSpace: 'pre-line',
  },
  baCardMeta: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.80)',
    marginTop: 12,
  },

  // Stress Test Protocol
  stressSection: {
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12,
    padding: '18px 20px',
    marginBottom: 16,
    background: 'rgba(255,255,255,0.02)',
  },
  stressHeader: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 4,
  },
  stressHint: {
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 14,
  },
  statusBtnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 6,
  },
  statusBtn: {
    background: 'rgba(255,255,255,0.05)',
    color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '9px 18px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },

  // QC Checklist
  qcProgressWrap: {
    marginBottom: 20,
  },
  qcProgressTrack: {
    width: '100%',
    height: 26,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    overflow: 'hidden',
  },
  qcProgressFill: {
    height: '100%',
    minWidth: 34,
    borderRadius: 999,
    background: 'linear-gradient(90deg, #16c95f, #38ef7d)',
    color: '#04210f',
    fontSize: 13,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'width 200ms ease',
  },
  qcProgressLabel: {
    fontSize: 13,
    color: '#94a3b8',
    marginTop: 8,
    textAlign: 'center',
  },
  qcCategory: {
    marginBottom: 18,
  },
  qcCategoryTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#7dd3fc',
    marginBottom: 10,
  },
  qcItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    cursor: 'pointer',
  },
  qcBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(255,255,255,0.04)',
    color: '#04210f',
    fontSize: 14,
    fontWeight: 900,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  qcBoxOn: {
    background: '#4ade80',
    borderColor: '#4ade80',
  },
  qcItemText: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.5,
  },
  repairDesc: {
    display: 'block',
    fontSize: 12,
    color: '#94a3b8',
    lineHeight: 1.4,
    marginTop: 2,
  },
};
